import React, { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../utils/api";

/* ----------------------------- helpers ----------------------------- */
const clamp = (v, lo = 0, hi = 1e9) =>
  Math.max(lo, Math.min(hi, Number.isFinite(+v) ? +v : lo));

const isValidMlbSalary = (site, v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return false;

  if (site === "dk") return n >= 2000 && n <= 15000;
  if (site === "fd") return n >= 2000 && n <= 12000;

  return n >= 2000 && n <= 15000;
};

const num = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v)
    .replace(/\$/g, "")
    .replace(/[,  \s]/g, "")
    .replace(/%/g, "")
    .trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const pctFrac = (v) => {
  if (v == null || v === "") return 0;
  const s = String(v).trim();
  if (!s) return 0;
  const stripped = s.replace(/[%\s]/g, "");
  const n = Number(stripped);
  if (!Number.isFinite(n)) return 0;
  if (/%/.test(s)) return clamp(n / 100, 0, 1);
  return n <= 1 ? clamp(n, 0, 1) : clamp(n / 100, 0, 1);
};

const fmt0 = (n) => (Number.isFinite(n) ? n.toLocaleString() : "—");
const fmt1 = (n) => (Number.isFinite(n) ? Number(n).toFixed(1) : "—");

const cx = (...classes) => classes.filter(Boolean).join(" ");

const escapeCsvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = (rows) =>
  rows.map((r) => r.map(escapeCsvCell).join(",")).join("\n");

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvToRows(text) {
  const out = [];
  let i = 0;
  let cur = "";
  let row = [];
  let inQuotes = false;

  while (i < text.length) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cur += '"';
        i += 2;
        continue;
      }
      if (ch === '"') {
        inQuotes = false;
        i += 1;
        continue;
      }
      cur += ch;
      i += 1;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      row.push(cur);
      cur = "";
      i += 1;
      continue;
    }
    if (ch === "\n") {
      row.push(cur);
      out.push(row);
      row = [];
      cur = "";
      i += 1;
      continue;
    }
    if (ch === "\r") {
      i += 1;
      continue;
    }

    cur += ch;
    i += 1;
  }

  row.push(cur);
  out.push(row);

  return out.filter((r) => r.some((x) => String(x || "").trim() !== ""));
}

const normTeam = (s) => String(s || "").toUpperCase().trim();

const stripAccents = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const normName = (s) =>
  stripAccents(String(s || ""))
    .toLowerCase()
    .replace(/\u2019/g, "'")
    .replace(/\./g, "")
    .replace(/,\s*(jr|sr)\b/g, "")
    .replace(/\b(jr|sr)\b/g, "")
    .replace(/[^a-z0-9' -]/g, "")
    .replace(/\s+/g, " ")
    .trim();

function parseMlbTimeToMinutes(timeStr) {
  const s0 = String(timeStr || "").trim();
  if (!s0) return NaN;

  const parsedDate = new Date(s0);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.getHours() * 60 + parsedDate.getMinutes();
  }

  const m = s0.match(/(\b\d{1,2})\s*:\s*(\d{2})\s*(AM|PM)?\b/i);
  if (m) {
    let h = Number(m[1]);
    const mi = Number(m[2]);
    const ap = (m[3] || "").toUpperCase();
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    if (h >= 0 && h <= 23 && mi >= 0 && mi <= 59) return h * 60 + mi;
  }

  const m2 = s0.match(/\b(\d{3,4})\b/);
  if (m2) {
    const raw = m2[1];
    const h = raw.length === 3 ? Number(raw.slice(0, 1)) : Number(raw.slice(0, 2));
    const mi = raw.length === 3 ? Number(raw.slice(1)) : Number(raw.slice(2));
    if (h >= 0 && h <= 23 && mi >= 0 && mi <= 59) return h * 60 + mi;
  }

  return NaN;
}

function formatMlbTimeET(timeStr) {
  const s = String(timeStr || "").trim();
  if (!s) return "";

  const parsedDate = new Date(s);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  const m12 = s.match(/\b(\d{1,2})\s*:\s*(\d{2})\s*(AM|PM)\b/i);
  if (m12) {
    const hh = Number(m12[1]);
    const mm = m12[2];
    const ap = m12[3].toUpperCase();
    return `${hh}:${mm} ${ap} ET`;
  }

  const m24 = s.match(/\b(\d{1,2})\s*:\s*(\d{2})(?:\s*:\s*\d{2})?\b/);
  if (m24) {
    let h = Number(m24[1]);
    const mi = m24[2];
    const ap = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${mi} ${ap} ET`;
  }

  return s;
}

function sameDayHasStarted(timeStr) {
  const raw = String(timeStr || "").trim();
  if (!raw) return false;

  const parsedDate = new Date(raw);
  if (!Number.isNaN(parsedDate.getTime())) {
    return Date.now() >= parsedDate.getTime();
  }

  const mins = parseMlbTimeToMinutes(raw);
  if (!Number.isFinite(mins)) return false;

  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  return nowMins >= mins;
}

function gameKey(a, b) {
  const A = normTeam(a);
  const B = normTeam(b);
  return A <= B ? `${A}|${B}` : `${B}|${A}`;
}

function pickFirst(obj, keys) {
  if (!obj || typeof obj !== "object") return undefined;
  for (const k of keys) {
    if (obj[k] != null && String(obj[k]).trim() !== "") return obj[k];
  }
  return undefined;
}

/* --------------------------- sticky state -------------------------- */
const useStickyState = (key, init) => {
  const initRef = React.useRef(init);

  const readFromStorage = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return initRef.current;
      const parsed = JSON.parse(raw);
      return parsed ?? initRef.current;
    } catch {
      return initRef.current;
    }
  }, [key]);

  const [v, setV] = useState(() => readFromStorage());

  // Only re-read when the storage key itself changes
  useEffect(() => {
    setV(readFromStorage());
  }, [key, readFromStorage]);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {}
  }, [key, v]);

  return [v, setV];
};

const useStickySet = (key, init = []) => {
  const [arr, setArr] = useStickyState(key, init);
  const set = useMemo(() => new Set(arr), [arr]);

  const setSet = (updater) => {
    setArr((prevArr) => {
      const prevSet = new Set(prevArr || []);
      const next =
        typeof updater === "function"
          ? updater(prevSet)
          : updater instanceof Set
          ? updater
          : new Set(updater || []);
      return Array.from(next || []);
    });
  };

  return [set, setSet];
};

const useStickyDict = (key, initObj = {}) => useStickyState(key, initObj);

/* ------------------------------ fetchers ------------------------------ */
function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.text();
        const cleaned = raw.replace(/^\uFEFF/, "").trim();
        const j = cleaned ? JSON.parse(cleaned) : null;
        if (alive) {
          setData(j);
          setErr(null);
        }
      } catch (e) {
        if (alive) {
          setData(null);
          setErr(e);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);

  return { data, err };
}

function useLastUpdatedFromMeta(metaUrl) {
  const [text, setText] = useState("—");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const j = await r.json();
        const iso =
          j?.updated_iso ||
          j?.updated_utc ||
          j?.source_mtime_iso ||
          j?.last_updated ||
          j?.generated_at ||
          j?.timestamp;

        if (!iso) return;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return;

        const t = d.toLocaleString(undefined, {
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
        if (alive) setText(t);
      } catch {}
    })();

    return () => {
      alive = false;
    };
  }, [metaUrl]);

  return text;
}

/* ----------------------------- sources ----------------------------- */
const PROJ_SRC = "/data/mlb/latest/projections.json";
const SITE_IDS_SRC = "/data/mlb/latest/site_ids.json";
const BATTER_DATA_SRC = "/data/mlb/latest/batter_data.json";
const PITCHER_DATA_SRC = "/data/mlb/latest/pitcher_data.json";
const META_SRC = "/data/mlb/latest/meta.json";

/* ------------------------------ sites ------------------------------ */
const SITES = {
  dk: {
    key: "dk",
    label: "DraftKings",
    logo: "/logos/dk.png",
    cap: 50000,
    slots: [
      { name: "P1", eligible: ["P"] },
      { name: "P2", eligible: ["P"] },
      { name: "C", eligible: ["C"] },
      { name: "1B", eligible: ["1B"] },
      { name: "2B", eligible: ["2B"] },
      { name: "3B", eligible: ["3B"] },
      { name: "SS", eligible: ["SS"] },
      { name: "OF1", eligible: ["OF"] },
      { name: "OF2", eligible: ["OF"] },
      { name: "OF3", eligible: ["OF"] },
    ],
    exportHeader: ["P", "P", "C", "1B", "2B", "3B", "SS", "OF", "OF", "OF"],
    posKeys: ["DK Pos", "Pos", "Position", "pos", "dk_pos"],
    projKeys: ["DK Proj", "Projection", "proj", "Points"],
    pownKeys: ["DK pOWN%", "DK pOWN", "dk_pown", "dk_pown_pct"],
    salKeys: ["DK Sal", "DK Salary", "salary", "dk_salary"],
    floorKeys: ["DK Floor", "Floor"],
    ceilKeys: ["DK Ceiling", "Ceiling"],
  },
  fd: {
    key: "fd",
    label: "FanDuel",
    logo: "/logos/fd.png",
    cap: 35000,
    slots: [
      { name: "P", eligible: ["P"] },
      { name: "C/1B", eligible: ["C", "1B"] },
      { name: "2B", eligible: ["2B"] },
      { name: "3B", eligible: ["3B"] },
      { name: "SS", eligible: ["SS"] },
      { name: "OF1", eligible: ["OF"] },
      { name: "OF2", eligible: ["OF"] },
      { name: "OF3", eligible: ["OF"] },
      { name: "UTIL", eligible: ["C", "1B", "2B", "3B", "SS", "OF"] },
    ],
    exportHeader: ["P", "C/1B", "2B", "3B", "SS", "OF", "OF", "OF", "UTIL"],
    posKeys: ["FD Pos", "Pos", "Position", "pos", "fd_pos"],
    projKeys: ["FD Proj", "Projection", "proj", "Points"],
    pownKeys: ["FD pOWN%", "FD pOWN", "fd_pown", "fd_pown_pct"],
    salKeys: ["FD Sal", "FD Salary", "salary", "fd_salary"],
    floorKeys: ["FD Floor", "Floor"],
    ceilKeys: ["FD Ceiling", "Ceiling"],
  },
};

function splitEligible(raw) {
  const txt = String(raw || "").toUpperCase();
  if (!txt) return [];
  return Array.from(
    new Set(
      txt
        .replace(/\bSP\b/g, "P")
        .replace(/\bRP\b/g, "P")
        .split(/[\/,;|]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    )
  );
}

function isPitcherRow(r, eligible = []) {
  if (eligible.includes("P")) return true;
  const flags = ["isPitcher", "Pitcher", "is_pitcher", "is_p"].some((k) => {
    const v = r?.[k];
    return v === true || v === 1 || String(v).toLowerCase() === "true";
  });
  if (flags) return true;
  const statKeys = ["IP", "ip", "ERA", "FIP", "xFIP", "pitch_count", "WHIP"];
  if (statKeys.some((k) => r?.[k] != null)) return true;
  return false;
}

/* ------------------------ site ids / export ids ------------------------ */
function buildIdIndex(siteIds) {
  const out = {
    dk: new Map(),
    fd: new Map(),
    fdDisplay: new Map(),
    fdPrefix: null,
  };

  const dkList = Array.isArray(siteIds?.dk) ? siteIds.dk : siteIds?.sites?.dk ?? [];
  const fdList = Array.isArray(siteIds?.fd) ? siteIds.fd : siteIds?.sites?.fd ?? [];

  for (const r of dkList) {
    const id = String(r.id ?? r.ID ?? r.dk_id ?? r["DK ID"] ?? "").trim();
    const name = String(r.name ?? r.player ?? r.Player ?? "").trim();
    if (id && name) out.dk.set(name, id);
  }

  const counts = new Map();
  for (const r of fdList) {
    const px = r.slateId ?? r.slate_id ?? r.groupId ?? r.group_id ?? r.lid ?? r.prefix ?? null;
    if (px != null && px !== "") counts.set(String(px), (counts.get(String(px)) || 0) + 1);
  }
  if (counts.size === 1) out.fdPrefix = [...counts.keys()][0];
  else if (counts.size > 1) out.fdPrefix = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];

  for (const r of fdList) {
    const id = String(r.id ?? r.ID ?? r.fd_id ?? r["FD ID"] ?? "").trim();
    const name = String(r.name ?? r.player ?? r.Player ?? "").trim();
    const display = String(r.displayName ?? r.Name ?? name).trim();
    if (id && name) {
      out.fd.set(name, id);
      out.fdDisplay.set(name, display);
    }
  }

  return out;
}

function fdNameWithIdRaw(name, id, prefix, display) {
  const outId = prefix ? `${prefix}-${id}` : id;
  return `${outId}:${display || name}`;
}

function parseExportCell(site, cell) {
  const raw = String(cell || "").trim();
  if (!raw) return { name: "", playerId: "", slateCode: "", lockedTag: false };

  if (site === "dk") {
    let s = raw;
    let lockedTag = false;

    if (/\(LOCKED\)\s*$/i.test(s)) {
      lockedTag = true;
      s = s.replace(/\(LOCKED\)\s*$/i, "").trim();
    }

    const m = s.match(/^(.*?)\s*\(([^()]+)\)\s*$/);
    if (m) {
      return {
        name: m[1].trim(),
        playerId: m[2].trim(),
        slateCode: "",
        lockedTag,
      };
    }

    if (/^\d+$/.test(s)) {
      return { name: "", playerId: s, slateCode: "", lockedTag };
    }

    return { name: s, playerId: "", slateCode: "", lockedTag };
  }

  let s = raw;
  let lockedTag = false;

  if (/\(LOCKED\)\s*$/i.test(s)) {
    lockedTag = true;
    s = s.replace(/\(LOCKED\)\s*$/i, "").trim();
  }

  const colonIdx = s.indexOf(":");
  if (colonIdx >= 0) {
    const left = s.slice(0, colonIdx).trim();
    const name = s.slice(colonIdx + 1).trim();

    const dashIdx = left.lastIndexOf("-");
    if (dashIdx >= 0) {
      return {
        name,
        slateCode: left.slice(0, dashIdx).trim(),
        playerId: left.slice(dashIdx + 1).trim(),
        lockedTag,
      };
    }

    return {
      name,
      slateCode: "",
      playerId: left,
      lockedTag,
    };
  }

  const slateIdOnly = s.match(/^(.+)-(\d+)$/);
  if (slateIdOnly) {
    return {
      name: "",
      playerId: slateIdOnly[2].trim(),
      slateCode: slateIdOnly[1].trim(),
      lockedTag,
    };
  }

  if (/^\d+$/.test(s)) {
    return {
      name: "",
      playerId: s,
      slateCode: "",
      lockedTag,
    };
  }

  return { name: s, playerId: "", slateCode: "", lockedTag };
}

function formatExportCell(site, item, idIndex) {
  const name = String(item?.name || "").trim();
  const playerId = String(item?.playerId || "").trim();
  const slateCode = String(item?.slateCode || "").trim();
  if (!name) return "";

  if (site === "dk") {
    return playerId ? `${name} (${playerId})` : name;
  }

  const display = idIndex?.fdDisplay?.get(name) || name;
  if (playerId && slateCode) return fdNameWithIdRaw(name, playerId, slateCode, display);
  if (playerId && idIndex?.fdPrefix) {
    return fdNameWithIdRaw(name, playerId, idIndex.fdPrefix, display);
  }
  return name;
}

function formatExportCellPreserveOriginal(site, item, idIndex, originalItem, keepOriginal) {
  if (keepOriginal) {
    const raw = String(originalItem?.originalCell || "").trim();
    if (raw) return raw;
  }

  return formatExportCell(site, item, idIndex);
}

function getContestSlotIndexes(headerRaw, site) {
  const hdr = (headerRaw || []).map((x) => String(x || "").trim().toUpperCase());
  const wantedBase =
    site === "dk"
      ? ["P", "P", "C", "1B", "2B", "3B", "SS", "OF", "OF", "OF"]
      : ["P", "C/1B", "2B", "3B", "SS", "OF", "OF", "OF", "UTIL"];

  const used = new Set();
  const indexes = [];

  for (const want of wantedBase) {
    let found = -1;
    for (let i = 0; i < hdr.length; i++) {
      if (used.has(i)) continue;
      if (hdr[i] === want) {
        found = i;
        break;
      }
    }
    if (found < 0) return null;
    used.add(found);
    indexes.push(found);
  }

  return indexes;
}

function getHeaderIndex(header, names) {
  const canon = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const map = new Map((header || []).map((h, i) => [canon(h), i]));
  for (const n of names) {
    const idx = map.get(canon(n));
    if (idx != null) return idx;
  }
  return -1;
}

function canonicalSlotOrder(site) {
  return site === "dk"
    ? ["P1", "P2", "C", "1B", "2B", "3B", "SS", "OF1", "OF2", "OF3"]
    : ["P", "C/1B", "2B", "3B", "SS", "OF1", "OF2", "OF3", "UTIL"];
}

function parseUploadedCsv(text, site) {
  const rows = csvToRows(text);
  if (!rows.length) throw new Error("CSV is empty.");

  const headerRaw = rows[0] || [];
  const cleanHeader = headerRaw.map((x) => String(x || "").trim());
  const expectedHeader = SITES[site].exportHeader;
  const isLineups =
    cleanHeader.length >= expectedHeader.length &&
    expectedHeader.every(
      (v, i) => String(cleanHeader[i] || "").trim().toUpperCase() === v.toUpperCase()
    );

  if (isLineups) {
    const expected = canonicalSlotOrder(site);
    const lineups = rows.slice(1).map((r, idx) => {
      const assignment = expected.map((slot, i) => {
        const parsed = parseExportCell(site, r[i] || "");
        return {
          slot,
          originalCell: r[i] || "",
          name: parsed.name,
          playerId: parsed.playerId,
          slateCode: parsed.slateCode,
        };
      });

      return {
        index: idx + 1,
        id: `uploaded_${idx + 1}`,
        sourceRowIndex: idx + 1,
        assignment,
      };
    });

    return {
      kind: "lineups",
      header: headerRaw,
      rawRows: rows,
      lineups,
      contests: [],
      selectedContestName: "",
      contestNameIdx: -1,
      entryIdIdx: -1,
      slotIndexes: expected.map((_, i) => i),
    };
  }

  const entryIdIdx = getHeaderIndex(headerRaw, ["Entry ID", "EntryId", "Entry"]);
  const contestNameIdx = getHeaderIndex(headerRaw, ["Contest Name", "Contest"]);
  const slotIndexes = getContestSlotIndexes(headerRaw, site);

  if (contestNameIdx >= 0 && slotIndexes) {
    const expected = canonicalSlotOrder(site);
    const byContest = new Map();

rows.slice(1).forEach((r, dataIdx) => {
  const sourceRowIndex = dataIdx + 1;
  const entryId = entryIdIdx >= 0 ? String(r[entryIdIdx] || "").trim() : "";
  const contestNameRaw = String(r[contestNameIdx] || "").trim();
  const hasAnyLineupCell = slotIndexes.some((idx) => String(r[idx] || "").trim() !== "");

  // Skip non-entry rows like template/player-list/blank rows
  if (!entryId || !contestNameRaw || !hasAnyLineupCell) return;

  const contestName = contestNameRaw;

  const assignment = expected.map((slot, j) => {
    const cellIdx = slotIndexes[j];
    const raw = r[cellIdx] || "";
    const parsed = parseExportCell(site, raw);
    return {
      slot,
      originalCell: raw,
      name: parsed.name,
      playerId: parsed.playerId,
      slateCode: parsed.slateCode,
    };
  });

  const lineup = {
    index: 0,
    id: `contest_${sourceRowIndex}_${entryId}`,
    sourceRowIndex,
    entryId,
    contestName,
    assignment,
  };

  if (!byContest.has(contestName)) byContest.set(contestName, []);
  byContest.get(contestName).push(lineup);
});

    const contests = Array.from(byContest.entries())
      .map(([name, list]) => ({
        name,
        count: list.length,
        lineups: list.map((x, i) => ({ ...x, index: i + 1 })),
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    return {
      kind: "contest",
      header: headerRaw,
      rawRows: rows,
      contests,
      selectedContestName: contests[0]?.name || "",
      contestNameIdx,
      entryIdIdx,
      slotIndexes,
      lineups: contests[0]?.lineups || [],
    };
  }

  throw new Error(
    `Could not detect CSV type for ${site.toUpperCase()}. Upload either a plain lineup export or a contest entries CSV.`
  );
}

/* ----------------------------- theming ----------------------------- */
const OPT_LIGHT_THEME = {
  page: "min-h-screen bg-slate-100 text-slate-900",
  headerText: "text-slate-900",
  subtext: "text-slate-600",
};

const OPT_DARK_THEME = {
  page: "min-h-screen bg-slate-950 text-slate-100",
  headerText: "text-slate-100",
  subtext: "text-slate-400",
};

const UI = {
  card: "rounded-2xl border shadow-sm",
  btn: {
    primary: "h-9 px-4 rounded-md font-semibold",
    ghost: "h-9 px-3 rounded-md border text-sm font-semibold",
  },
  inputBase:
    "h-9 px-3 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-sky-300/40 focus:border-sky-400",
};

const TEAM_COLORS = {
  ARI: "#A71930", ATL: "#CE1141", BAL: "#DF4601", BOS: "#BD3039",
  CHC: "#0E3386", CIN: "#C6011F", CLE: "#00385D", COL: "#33006F",
  CWS: "#27251F", DET: "#0C2340", HOU: "#002D62", KC: "#004687",
  LAA: "#BA0021", LAD: "#005A9C", MIA: "#00A3E0", MIL: "#12284B",
  MIN: "#002B5C", NYM: "#002D72", NYY: "#003087", ATH: "#003831",
  PHI: "#E81828", PIT: "#FDB827", SD: "#2F241D", SEA: "#0C2C56",
  SF: "#FD5A1E", STL: "#C41E3A", TB: "#092C5C", TEX: "#003278",
  TOR: "#134A8E", WSH: "#AB0003",
};

const chipStyle = (abbr, active = false) => {
  const bg = TEAM_COLORS[abbr] || "#64748b";
  return {
    background: bg,
    color: "#fff",
    borderRadius: 6,
    padding: "1px 7px",
    fontSize: "11px",
    lineHeight: "16px",
    border: active ? "2px solid rgba(255,255,255,0.85)" : "1px solid rgba(0,0,0,0.08)",
    boxShadow: active ? "0 0 0 2px rgba(0,0,0,0.08) inset" : "none",
  };
};

/* --------------------------- row ordering --------------------------- */
function orderPlayersForSite(site, names, rowsMap) {
  const pool = names.map((n) => rowsMap.get(n)).filter(Boolean);
  const take = (pred) => {
    const i = pool.findIndex(pred);
    if (i === -1) return null;
    return pool.splice(i, 1)[0];
  };

  const out = [];
  if (site === "dk") {
    out.push(take((r) => r.eligible.includes("P")));
    out.push(take((r) => r.eligible.includes("P")));
    out.push(take((r) => r.eligible.includes("C")));
    out.push(take((r) => r.eligible.includes("1B")));
    out.push(take((r) => r.eligible.includes("2B")));
    out.push(take((r) => r.eligible.includes("3B")));
    out.push(take((r) => r.eligible.includes("SS")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(take((r) => r.eligible.includes("OF")));
  } else {
    out.push(take((r) => r.eligible.includes("P")));
    out.push(take((r) => r.eligible.includes("C") || r.eligible.includes("1B")));
    out.push(take((r) => r.eligible.includes("2B")));
    out.push(take((r) => r.eligible.includes("3B")));
    out.push(take((r) => r.eligible.includes("SS")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(take((r) => r.eligible.includes("OF")));
    out.push(
      take((r) => ["C", "1B", "2B", "3B", "SS", "OF"].some((p) => r.eligible.includes(p)))
    );
  }

  return out.filter(Boolean).concat(pool);
}

/* -------------------------- backend call -------------------------- */
async function runLateSwapBackend(payload) {
  const res = await fetch(`${API_BASE}/solve_mlb_late_swap`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`Late swap failed: HTTP ${res.status}`);
  }

  const data = await res.json();
  if (!data?.success) {
    throw new Error(data?.reason || "No feasible late swap");
  }

  return data;
}

function normalizeBackendAssignment(site, assignment) {
  const order = canonicalSlotOrder(site);
  const bySlot = new Map(
    (assignment || []).map((x) => [String(x.slot || "").trim(), String(x.name || "").trim()])
  );
  return order.map((slot) => ({
    slot,
    name: bySlot.get(slot) || "",
  }));
}

function buildUsageTables(lineups) {
  const playerCounts = new Map();
  const stackCounts = new Map();
  const stackSizeTotals = new Map();
  const totalLineups = Math.max(lineups?.length || 0, 1);

  for (const lineup of lineups || []) {
    const arr = lineup.finalAssignment || lineup.assignment || [];

    const hitterCountsByTeam = new Map();

    for (const a of arr) {
      const nm = String(a?.name || "").trim();
      if (nm) {
        playerCounts.set(nm, {
          name: nm,
          team: a?.team || "",
          pos: a?.posDisp || "",
          count: (playerCounts.get(nm)?.count || 0) + 1,
        });
      }

      const tm = String(a?.team || "").trim();
      const isPitcher = !!a?.isPitcher;

      if (tm && !isPitcher) {
        hitterCountsByTeam.set(tm, (hitterCountsByTeam.get(tm) || 0) + 1);
      }
    }

    for (const [team, cnt] of hitterCountsByTeam.entries()) {
      if (cnt >= 2) {
        stackCounts.set(team, (stackCounts.get(team) || 0) + 1);
        stackSizeTotals.set(team, (stackSizeTotals.get(team) || 0) + cnt);
      }
    }
  }

  const players = Array.from(playerCounts.values())
    .map((x) => ({
      ...x,
      exposure: (x.count / totalLineups) * 100,
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const stacks = Array.from(stackCounts.entries())
    .map(([team, count]) => ({
      team,
      count,
      exposure: (count / totalLineups) * 100,
      avgStackSize: (stackSizeTotals.get(team) || 0) / count,
    }))
    .sort((a, b) => b.exposure - a.exposure || a.team.localeCompare(b.team));

  return { players, stacks };
}

function getExportParts({ site, slate, row, originalItem, samePlayer }) {
  if (samePlayer) {
    return {
      playerId: String(originalItem?.playerId || "").trim(),
      slateCode: String(originalItem?.slateCode || "").trim(),
    };
  }

  const mainId = String(row?.site_id_main || "").trim();
  const earlyId = String(row?.site_id_early || "").trim();
  const lateId = String(row?.site_id_late || "").trim();
  const rawSlateCode = String(row?.slateCode || "").trim();

  const chosenId =
    slate === "early"
      ? earlyId
      : slate === "late"
      ? lateId
      : mainId;

  if (site === "fd") {
    const dashIdx = chosenId.lastIndexOf("-");

    if (dashIdx >= 0) {
      return {
        slateCode: chosenId.slice(0, dashIdx).trim(),
        playerId: chosenId.slice(dashIdx + 1).trim(),
      };
    }

    return {
      slateCode: rawSlateCode,
      playerId: chosenId,
    };
  }

  return {
    slateCode: rawSlateCode,
    playerId: chosenId,
  };
}

/* ------------------------------- page ------------------------------- */
export default function MLBLateSwap({ activeTab = "lateSwap", setActiveTab = () => {} }) {
  const { data: proj } = useJson(PROJ_SRC);
  const { data: siteIds } = useJson(SITE_IDS_SRC);
  const { data: batterData } = useJson(BATTER_DATA_SRC);
  const { data: pitcherData } = useJson(PITCHER_DATA_SRC);
  const updatedAtText = useLastUpdatedFromMeta(META_SRC);

  const [hideExcluded, setHideExcluded] = useState(false);
  const [theme, setTheme] = useStickyState("mlb.lateswap.theme", "dark");
  const isDark = theme === "dark";
  const T = isDark ? OPT_DARK_THEME : OPT_LIGHT_THEME;

  const cardClass = cx(
    UI.card,
    isDark
      ? "border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
      : "border-slate-200 bg-white"
  );

  const ghostBtnClass = cx(
    UI.btn.ghost,
    isDark
      ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
      : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
  );

  const primaryBtnClass = cx(
    UI.btn.primary,
    "bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60"
  );

  const inputClass = cx(
    UI.inputBase,
    isDark
      ? "bg-slate-950/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
      : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
  );

  const pillBase =
    "px-3 py-1.5 rounded-full border text-sm inline-flex items-center gap-2 transition backdrop-blur-md";
  const pillOn = isDark
    ? "bg-sky-500/15 border-sky-400/40 text-sky-100"
    : "bg-sky-500/20 border-sky-500/30 text-sky-900";
  const pillOff = isDark
    ? "bg-slate-900/40 border-slate-700/70 text-slate-200 hover:bg-slate-900/60"
    : "bg-white/60 border-slate-300 text-slate-700 hover:bg-slate-50";

// Shared underline tabs for view, fantasy site, and slate.
const topSegWrap = cx(
  "inline-flex shrink-0 items-center gap-6 border-b",
  isDark ? "border-slate-800" : "border-slate-200"
);

const topSegBtn = (active) =>
  cx(
    "relative inline-flex h-9 items-center gap-2 whitespace-nowrap pb-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50",
    active
      ? cx(isDark ? "text-white" : "text-slate-950",
          "after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-sky-400")
      : isDark
      ? "text-slate-400 hover:text-slate-100"
      : "text-slate-500 hover:text-slate-900"
  );

const siteSegBtn = topSegBtn;
const slateSegWrap = topSegWrap;
const slateSegBtn = topSegBtn;

  const [site, setSite] = useStickyState("mlb.lateswap.site", "dk");
  const [slate, setSlate] = useStickyState("mlb.lateswap.slate", "main");
  const CFG = SITES[site];

  const [csvName, setCsvName] = useStickyState("mlb.lateswap.csvName", "");
  const [parseError, setParseError] = useStickyState("mlb.lateswap.parseError", "");
  const [statusMsg, setStatusMsg] = useStickyState("mlb.lateswap.statusMsg", "");
  const [uploadBundle, setUploadBundle] = useState(null);
  const [selectedContestName, setSelectedContestName] = useStickyState(
    "mlb.lateswap.selectedContestName",
    ""
  );
  const [importedLineups, setImportedLineups] = useStickyState("mlb.lateswap.importedLineups", []);
  const [swappedLineups, setSwappedLineups] = useStickyState("mlb.lateswap.swappedLineups", []);
  const [savedBuilds, setSavedBuilds] = useStickyState("mlb.lateswap.savedBuilds", []);
  const fileRef = useRef(null);

  const [running, setRunning] = useState(false);
  const [showOnlyChanged, setShowOnlyChanged] = useStickyState("mlb.lateswap.showOnlyChanged", false);
  const [useAutoStarted, setUseAutoStarted] = useStickyState("mlb.lateswap.useAutoStarted", true);

  const [q, setQ] = useStickyState("mlb.lateswap.q", "");
  const [posFilter, setPosFilter] = useStickyState("mlb.lateswap.posFilter", "ALL");
  const [hideUnprojected, setHideUnprojected] = useStickyState(
    `mlb.lateswap.${site}.${slate}.hideUnprojected`,
    true
  );
  const [sort, setSort] = useStickyState("mlb.lateswap.sort", { col: "eproj", dir: "desc" });

  const [locks, setLocks] = useStickySet(`mlb.lateswap.${site}.${slate}.locks`, []);
  const [excls, setExcls] = useStickySet(`mlb.lateswap.${site}.${slate}.excls`, []);
  const [boost, setBoost] = useStickyDict(`mlb.lateswap.${site}.${slate}.boost`, {});
  const [minExp, setMinExp] = useStickyDict(`mlb.lateswap.${site}.${slate}.minExp`, {});
  const [maxExp, setMaxExp] = useStickyDict(`mlb.lateswap.${site}.${slate}.maxExp`, {});
  const [lockedGames, setLockedGames] = useStickySet(`mlb.lateswap.${site}.${slate}.lockedGames`, []);

  const [randomness, setRandomness] = useStickyState(`mlb.lateswap.${site}.${slate}.randomness`, 0);
  const [lineupPownMax, setLineupPownMax] = useStickyState(
    `mlb.lateswap.${site}.${slate}.lineupPownMax`,
    ""
  );
  const [maxPlayerPown, setMaxPlayerPown] = useStickyState(
    `mlb.lateswap.${site}.${slate}.maxPlayerPown`,
    ""
  );
  const [maxFromTeam, setMaxFromTeam] = useStickyState(
    `mlb.lateswap.${site}.${slate}.maxFromTeam`,
    ""
  );
  const [avoidHittersVsOppP, setAvoidHittersVsOppP] = useStickyState(
    `mlb.lateswap.${site}.${slate}.avoidHittersVsOppP`,
    true
  );
  const [maxHittersVsOppP, setMaxHittersVsOppP] = useStickyState(
    `mlb.lateswap.${site}.${slate}.maxHittersVsOppP`,
    0
  );

  const [stackMode, setStackMode] = useStickyState(`mlb.lateswap.${site}.${slate}.stackMode`, "preserve");
  const [preservePrimaryTeam, setPreservePrimaryTeam] = useStickyState(
    `mlb.lateswap.${site}.${slate}.preservePrimaryTeam`,
    true
  );
  const [preserveSecondaryTeam, setPreserveSecondaryTeam] = useStickyState(
    `mlb.lateswap.${site}.${slate}.preserveSecondaryTeam`,
    true
  );

  const [teamBoost, setTeamBoost] = useStickyDict(
  `mlb.lateswap.${site}.${slate}.teamBoost`,
  {}
);

const [excludedGames, setExcludedGames] = useStickySet(
  `mlb.lateswap.${site}.${slate}.excludedGames`,
  []
);

const [excludedTeams, setExcludedTeams] = useStickySet(
  `mlb.lateswap.${site}.${slate}.excludedTeams`,
  []
);

const [selectedGamesArr, setSelectedGamesArr] = useStickyState(
  `mlb.lateswap.${site}.${slate}.selectedGames`,
  []
);

const selectedGames = useMemo(() => new Set(selectedGamesArr), [selectedGamesArr]);

const setSelectedGames = (arg) => {
  setSelectedGamesArr((prevArr) => {
    const prevSet = new Set(prevArr || []);
    let nextSet;
    if (typeof arg === "function") nextSet = arg(prevSet);
    else if (arg instanceof Set) nextSet = arg;
    else if (Array.isArray(arg)) nextSet = new Set(arg);
    else nextSet = new Set();
    return Array.from(nextSet);
  });
};

  const idIndex = useMemo(() => buildIdIndex(siteIds || {}), [siteIds]);

  /* ------------------------ site meta / ids ------------------------ */
  const siteMeta = useMemo(() => {
    const out = {
      dk: { byName: new Map(), byId: new Map() },
      fd: { byName: new Map(), byId: new Map() },
    };

    const dkRows = Array.isArray(siteIds?.dk) ? siteIds.dk : siteIds?.sites?.dk ?? [];
    const fdRows = Array.isArray(siteIds?.fd) ? siteIds.fd : siteIds?.sites?.fd ?? [];

    const ingest = (siteKey, arr) => {
      for (const r of arr) {
        const name = String(pickFirst(r, ["name", "player", "Player", "Name"]) || "").trim();
        if (!name) continue;

        const rec = {
          name,
          id: String(pickFirst(r, ["id", "ID", "dk_id", "fd_id", "site_id"]) || "").trim() || null,
          salary: num(pickFirst(r, ["salary", "Salary", "sal"]) || 0),
          early_id:
            String(pickFirst(r, ["early_id", "earlyId", "EARLY_ID", "early_site_id"]) || "").trim() ||
            null,
          early_salary: num(pickFirst(r, ["early_salary", "earlySalary", "EARLY_SALARY"]) || 0),
          late_id:
            String(pickFirst(r, ["late_id", "lateId", "LATE_ID", "late_site_id"]) || "").trim() ||
            null,
          late_salary: num(pickFirst(r, ["late_salary", "lateSalary", "LATE_SALARY"]) || 0),
          pos: String(pickFirst(r, ["pos", "Pos", "position", "Position", "rosterPosition"]) || "")
            .toUpperCase()
            .replace(/\bSP\b/g, "P")
            .replace(/\bRP\b/g, "P")
            .replace(/[;,|]/g, "/")
            .replace(/\s+/g, "")
            .trim(),
          slateCode: String(
            pickFirst(r, ["slateId", "slate_id", "groupId", "group_id", "lid", "prefix"]) || ""
          ).trim(),
          displayName: String(pickFirst(r, ["displayName", "Name"]) || name).trim(),
          team: normTeam(pickFirst(r, ["team", "Team", "Tm", "TEAM", "team_abbr", "TeamAbbrev"]) || ""),
          opp: normTeam(pickFirst(r, ["opp", "Opp", "OPP", "opponent", "Opponent"]) || ""),
          time: String(
            pickFirst(r, ["Time", "time", "Start", "Start Time", "StartTime", "Game Time", "GameTime", "ET"]) || ""
          ).trim(),
          battingOrder: String(
            pickFirst(r, [
              "battingOrder",
              "BattingOrder",
              "bo",
              "BO",
              "lineupOrder",
              "LineupOrder",
              "order",
              "Order",
              "batting_order",
              "LineupSpot",
              "spot",
            ]) || ""
          ).trim(),
        };

        out[siteKey].byName.set(name, rec);
        out[siteKey].byName.set(normName(name), rec);

        if (rec.id) out[siteKey].byId.set(rec.id, rec);
        if (rec.early_id) out[siteKey].byId.set(rec.early_id, rec);
        if (rec.late_id) out[siteKey].byId.set(rec.late_id, rec);
      }
    };

    ingest("dk", dkRows);
    ingest("fd", fdRows);

    return out;
  }, [siteIds]);

  function getSlateIdAndSalary(meta, slateKey) {
    if (!meta) return { id: "", salary: 0 };
    if (slateKey === "early") {
      return {
        id: String(meta.early_id || "").trim(),
        salary: num(meta.early_salary),
      };
    }
    if (slateKey === "late") {
      return {
        id: String(meta.late_id || "").trim(),
        salary: num(meta.late_salary),
      };
    }
    return {
      id: String(meta.id || "").trim(),
      salary: num(meta.salary),
    };
  }

function getSlateTags(meta, siteKey) {
  if (!meta) return [];
  const tags = [];

  if (String(meta.id || "").trim() && isValidMlbSalary(siteKey, meta.salary)) {
    tags.push("main");
  }
  if (String(meta.early_id || "").trim() && isValidMlbSalary(siteKey, meta.early_salary)) {
    tags.push("early");
  }
  if (String(meta.late_id || "").trim() && isValidMlbSalary(siteKey, meta.late_salary)) {
    tags.push("late");
  }

  return tags;
}

  /* ------------------------ game time maps ------------------------ */
  const timeByGameKey = useMemo(() => {
    const m = new Map();
    const add = (row) => {
      const team = normTeam(row?.Team ?? row?.team ?? row?.Tm);
      const opp = normTeam(row?.Opp ?? row?.opp ?? row?.OPP ?? row?.Opponent);
      const t =
        row?.Time ??
        row?.time ??
        row?.Start ??
        row?.StartTime ??
        row?.["Start Time"] ??
        row?.GameTime ??
        row?.["Game Time"];
      if (!team || !opp || !t) return;
      const key = gameKey(team, opp);
      const cur = m.get(key);
      const candMin = parseMlbTimeToMinutes(t);
      const curMin = cur ? parseMlbTimeToMinutes(cur) : NaN;
      const better =
        !cur ||
        (Number.isFinite(candMin) && !Number.isFinite(curMin)) ||
        (Number.isFinite(candMin) && Number.isFinite(curMin) && candMin < curMin);
      if (better) m.set(key, String(t));
    };

    (Array.isArray(batterData) ? batterData : []).forEach(add);
    (Array.isArray(pitcherData) ? pitcherData : []).forEach(add);
    return m;
  }, [batterData, pitcherData]);

  const probablePitcherByTeamGame = useMemo(() => {
    const m = new Map(); // key: `${game}|${team}` => pitcher name
    (Array.isArray(pitcherData) ? pitcherData : []).forEach((row) => {
      const team = normTeam(row?.Team ?? row?.team ?? row?.Tm);
      const opp = normTeam(row?.Opp ?? row?.opp ?? row?.OPP ?? row?.Opponent);
      const name =
        row?.Player ??
        row?.player ??
        row?.Name ??
        row?.name ??
        row?.Pitcher ??
        row?.pitcher ??
        "";
      if (!team || !opp || !name) return;
      const gk = gameKey(team, opp);
      m.set(`${gk}|${team}`, String(name).trim());
    });
    return m;
  }, [pitcherData]);

  const teamTotalByTeam = useMemo(() => {
  const m = new Map();

  for (const row of batterData || []) {
    const team = normTeam(row?.Team ?? row?.team ?? row?.Tm);
    const total = num(row?.Total);

    if (!team || !Number.isFinite(total) || total <= 0) continue;
    m.set(team, total);
  }

  return m;
}, [batterData]);

const teamGameInfoByTeam = useMemo(() => {
  const m = new Map();

  const add = (row) => {
    const team = normTeam(row?.Team ?? row?.team ?? row?.Tm);
    const opp = normTeam(row?.Opp ?? row?.opp ?? row?.OPP ?? row?.Opponent);
    const t =
      row?.Time ??
      row?.time ??
      row?.Start ??
      row?.StartTime ??
      row?.["Start Time"] ??
      row?.GameTime ??
      row?.["Game Time"] ??
      "";

    if (!team || !opp) return;

    const gk = gameKey(team, opp);
    const cur = m.get(team);
    const candMin = parseMlbTimeToMinutes(t);
    const curMin = cur ? parseMlbTimeToMinutes(cur.time) : NaN;
    const better =
      !cur ||
      (Number.isFinite(candMin) && !Number.isFinite(curMin)) ||
      (Number.isFinite(candMin) && Number.isFinite(curMin) && candMin < curMin);

    if (better) {
      m.set(team, { team, opp, game: gk, time: String(t || "") });
    }
  };

  (Array.isArray(batterData) ? batterData : []).forEach(add);
  (Array.isArray(pitcherData) ? pitcherData : []).forEach(add);
  return m;
}, [batterData, pitcherData]);
  /* ------------------------------ rows ------------------------------ */
  const rowsAll = useMemo(() => {
    const arr = Array.isArray(proj?.rows)
      ? proj.rows
      : Array.isArray(proj?.players)
      ? proj.players
      : Array.isArray(proj?.data)
      ? proj.data
      : Array.isArray(proj)
      ? proj
      : [];

    const siteKey = CFG.key;

    const projectedRows = arr
      .map((r) => {
        const name = r.player ?? r.Player ?? r.Name ?? r.playerName ?? r.name ?? "";
        if (!name) return null;

        const projIdRaw =
          r[`${siteKey}_id`] ??
          r[`${siteKey}Id`] ??
          r[`${siteKey.toUpperCase()} ID`] ??
          r[`${siteKey.toUpperCase()}_ID`] ??
          r.id ??
          r.ID ??
          "";

        const projId = String(projIdRaw ?? "").trim();
        const metaById = projId ? siteMeta?.[siteKey]?.byId?.get(projId) : null;
        const meta =
          metaById ||
          siteMeta?.[siteKey]?.byName?.get(name) ||
          siteMeta?.[siteKey]?.byName?.get(normName(name)) ||
          null;

        const slates = getSlateTags(meta, siteKey);
        const slateInfo = getSlateIdAndSalary(meta, slate);

        const rawPos =
          meta?.pos && String(meta.pos).trim() !== ""
            ? String(meta.pos)
            : String(pickFirst(r, CFG.posKeys) || "");

        let eligible = splitEligible(rawPos);
        if (!eligible.length && isPitcherRow(r, eligible)) eligible = ["P"];
        if (!eligible.length) return null;

        const team = normTeam(
          r.team ??
            r.Team ??
            r.Tm ??
            r.TEAM ??
            r.team_abbr ??
            r.TeamAbbrev ??
            meta?.team ??
            ""
        );
        const inferredInfo = team ? teamGameInfoByTeam.get(team) : null;
        const opp = normTeam(
          r.opp ?? r.Opp ?? r.OPP ?? r.opponent ?? r.Opponent ?? meta?.opp ?? inferredInfo?.opp ?? ""
        );

        const rawTime =
          r.Time ??
          r["Time ET"] ??
          r["Start"] ??
          r["Start Time"] ??
          r["StartTime"] ??
          r["Game Time"] ??
          r["GameTime"] ??
          r["ET"] ??
          r.time ??
          r.start_time ??
          r.startTime ??
          r.game_time ??
          r.GameStart ??
          r["GameStart"] ??
          meta?.time ??
          inferredInfo?.time ??
          "";

        const timeFromJson = team && opp ? timeByGameKey.get(gameKey(team, opp)) : "";
        const time = timeFromJson || rawTime || "";

        const salaryFromRow = num(
          r[`${siteKey}_sal`] ?? pickFirst(r, CFG.salKeys) ?? r.Salary ?? r.salary
        );

        let salary = 0;
        if (isValidMlbSalary(siteKey, slateInfo.salary)) salary = slateInfo.salary;
        else if (isValidMlbSalary(siteKey, salaryFromRow)) salary = salaryFromRow;
        else if (isValidMlbSalary(siteKey, meta?.salary)) salary = num(meta?.salary);

        const projVal = num(r[`${siteKey}_proj`] ?? pickFirst(r, CFG.projKeys));
        const floor = num(r[`${siteKey}_floor`] ?? pickFirst(r, CFG.floorKeys));
        const ceil = num(r[`${siteKey}_ceil`] ?? pickFirst(r, CFG.ceilKeys));
        const pown = pctFrac(r[`${siteKey}_pown`] ?? pickFirst(r, CFG.pownKeys));

        const isPitcher = eligible.includes("P");
        const battingOrderRaw =
          r.battingOrder ??
          r.BattingOrder ??
          r.bo ??
          r.BO ??
          r.lineupOrder ??
          r.LineupOrder ??
          r.order ??
          r.Order ??
          r.batting_order ??
          r.LineupSpot ??
          r.spot ??
          meta?.battingOrder ??
          null;

        const battingOrder =
          isPitcher
            ? "-"
            : battingOrderRaw != null && String(battingOrderRaw).trim() !== ""
            ? String(battingOrderRaw).trim()
            : "-";
        const val = Number.isFinite(projVal) && salary > 0 ? (projVal / salary) * 1000 : 0;
        const gk = team && opp ? gameKey(team, opp) : "";
        const oppPitcher =
          !isPitcher && gk && opp ? probablePitcherByTeamGame.get(`${gk}|${opp}`) || "" : "";

        return {
          name,
          team,
          opp,
          game: gk,
          time,
          eligible,
          isPitcher,
          battingOrder,
          salary,
          proj: projVal,
          eproj: projVal,
          floor,
          ceil,
          pown,
          val,
          slates,
          site_id_main: String(meta?.id || "").trim(),
          site_id_early: String(meta?.early_id || "").trim(),
          site_id_late: String(meta?.late_id || "").trim(),
          slateCode: String(meta?.slateCode || "").trim(),
          oppPitcher,
          hasProjection: true,
          siteIdOnly: false,
          __raw: r,
        };
      })
      .filter(Boolean);

    const seenNames = new Set(projectedRows.map((r) => normName(r.name)));
    const seenIds = new Set(
      projectedRows
        .flatMap((r) => [r.site_id_main, r.site_id_early, r.site_id_late])
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );

    const siteOnlyRows = [];
    const metaSeen = new Set();

    for (const rec of siteMeta?.[siteKey]?.byName?.values?.() || []) {
      if (!rec?.name) continue;

      const slateInfo = getSlateIdAndSalary(rec, slate);
      const slateId = String(slateInfo?.id || "").trim();
      const salary = isValidMlbSalary(siteKey, slateInfo?.salary)
        ? num(slateInfo.salary)
        : isValidMlbSalary(siteKey, rec.salary)
        ? num(rec.salary)
        : 0;

      const metaKey = slateId || normName(rec.name);
      if (!metaKey || metaSeen.has(metaKey)) continue;
      metaSeen.add(metaKey);
      if (seenNames.has(normName(rec.name)) || (slateId && seenIds.has(slateId))) continue;
      if (!slateId || !isValidMlbSalary(siteKey, salary)) continue;

      const eligible = splitEligible(rec.pos);
      if (!eligible.length) continue;

      const isPitcher = eligible.includes("P");
      const team = normTeam(rec.team);
      const inferredInfo = team ? teamGameInfoByTeam.get(team) : null;
      const opp = normTeam(rec.opp || inferredInfo?.opp || "");
      const gk = team && opp ? gameKey(team, opp) : "";
      const time = (team && opp ? timeByGameKey.get(gk) : "") || rec.time || inferredInfo?.time || "";
      const battingOrder = isPitcher ? "-" : rec.battingOrder || "-";
      const oppPitcher =
        !isPitcher && gk && opp ? probablePitcherByTeamGame.get(`${gk}|${opp}`) || "" : "";

      siteOnlyRows.push({
        name: String(rec.name).trim(),
        team,
        opp,
        game: gk,
        time,
        eligible,
        isPitcher,
        battingOrder,
        salary,
        proj: 0,
        eproj: 0,
        floor: 0,
        ceil: 0,
        pown: 0,
        val: 0,
        slates: getSlateTags(rec, siteKey),
        site_id_main: String(rec.id || "").trim(),
        site_id_early: String(rec.early_id || "").trim(),
        site_id_late: String(rec.late_id || "").trim(),
        slateCode: String(rec.slateCode || "").trim(),
        oppPitcher,
        hasProjection: false,
        siteIdOnly: true,
        __raw: rec,
      });
    }

    return [...projectedRows, ...siteOnlyRows];
  }, [proj, CFG, siteMeta, slate, timeByGameKey, teamGameInfoByTeam, probablePitcherByTeamGame]);


const rows = useMemo(() => {
  const filtered = rowsAll.filter(
    (r) =>
      Array.isArray(r.slates) &&
      r.slates.includes(slate) &&
      isValidMlbSalary(site, r.salary)
  );

  return filtered.map((r) => {
    const playerSteps = Number(boost[r.name] || 0);
    const teamSteps = !r.isPitcher ? Number(teamBoost[r.team] || 0) : 0;

    const boostedProj =
      r.proj * (1 + 0.075 * playerSteps + 0.10 * teamSteps);

    const boostedVal =
      Number.isFinite(boostedProj) && r.salary > 0
        ? (boostedProj / r.salary) * 1000
        : 0;

    return {
      ...r,
      eproj: boostedProj,
      val: boostedVal,
      boostSteps: playerSteps + teamSteps,
      playerBoostSteps: playerSteps,
      teamBoostSteps: teamSteps,
    };
  });
}, [rowsAll, slate, boost, teamBoost, site]);

  const rowsByName = useMemo(() => new Map(rows.map((r) => [r.name, r])), [rows]);

const rowByAlias = useMemo(() => {
  const m = new Map();

  for (const r of rows) {
    const nm = String(r.name || "").trim();
    if (nm) {
      m.set(normName(nm), r);
      m.set(nm, r);
    }

    const raw = r.__raw || {};

    const extraNames = [
      raw.name,
      raw.Name,
      raw.player,
      raw.Player,
      raw.playerName,
      raw.displayName,
      raw.DisplayName,
    ]
      .map((x) => String(x || "").trim())
      .filter(Boolean);

    for (const alias of extraNames) {
      m.set(normName(alias), r);
      m.set(alias, r);
    }

    const ids = [r.site_id_main, r.site_id_early, r.site_id_late]
      .map((x) => String(x || "").trim())
      .filter(Boolean);

    for (const id of ids) {
      m.set(`id:${id}`, r);
      if (r.slateCode) m.set(`id:${r.slateCode}-${id}`, r);
    }
  }

  return m;
}, [rows]);

const allGames = useMemo(() => {
  const best = new Map();

  for (const r of rows) {
    if (!r.team || !r.opp) continue;
    const key = r.game || gameKey(r.team, r.opp);
    if (!best.has(key)) {
      best.set(key, {
        key,
        a: r.team,
        b: r.opp,
        rawTime: r.time || "",
        displayTime: formatMlbTimeET(r.time),
      });
    }
  }

  return Array.from(best.values()).sort((x, y) => {
    const xm = parseMlbTimeToMinutes(x.rawTime);
    const ym = parseMlbTimeToMinutes(y.rawTime);
    const xa = Number.isFinite(xm) ? xm : 1e9;
    const ya = Number.isFinite(ym) ? ym : 1e9;
    return xa - ya || `${x.a}@${x.b}`.localeCompare(`${y.a}@${y.b}`);
  });
}, [rows]);

const playersByGame = useMemo(() => {
  const m = new Map();
  for (const r of rowsAll) {
    if (!r.game) continue;
    if (!m.has(r.game)) m.set(r.game, new Set());
    m.get(r.game).add(r.name);
  }
  return m;
}, [rowsAll]);

const playersByTeam = useMemo(() => {
  const m = new Map();
  for (const r of rowsAll) {
    if (!r.team) continue;
    if (!m.has(r.team)) m.set(r.team, new Set());
    m.get(r.team).add(r.name);
  }
  return m;
}, [rowsAll]);

const autoExcludedNames = useMemo(() => {
  const out = new Set();

  for (const g of excludedGames) {
    const set = playersByGame.get(g);
    if (set) for (const n of set) out.add(n);
  }

  for (const t of excludedTeams) {
    const set = playersByTeam.get(t);
    if (set) for (const n of set) out.add(n);
  }

  return out;
}, [excludedGames, excludedTeams, playersByGame, playersByTeam]);

const effectiveExcls = useMemo(() => {
  const out = new Set(excls);
  for (const n of autoExcludedNames) out.add(n);
  return out;
}, [excls, autoExcludedNames]);

const autoLockedGames = useMemo(() => {
  const s = new Set();
  if (!useAutoStarted) return s;
  for (const g of allGames) {
    if (sameDayHasStarted(g.rawTime)) s.add(g.key);
  }
  return s;
}, [allGames, useAutoStarted]);

  const effectiveLockedGames = useMemo(() => {
    const s = new Set(lockedGames);
    for (const g of autoLockedGames) s.add(g);
    return s;
  }, [lockedGames, autoLockedGames]);

  const toggleExcludeGame = (gKey) => {
  setExcludedGames((prev) => {
    const next = new Set(prev);
    next.has(gKey) ? next.delete(gKey) : next.add(gKey);
    return next;
  });
};

const toggleExcludeTeam = (teamAbbr) => {
  const t = normTeam(teamAbbr);
  if (!t) return;

  setExcludedTeams((prev) => {
    const next = new Set(prev);
    next.has(t) ? next.delete(t) : next.add(t);
    return next;
  });
};

const bumpTeamBoost = (team, step) =>
  setTeamBoost((m) => ({
    ...m,
    [team]: clamp((m[team] || 0) + step, -6, 6),
  }));

  /* ----------------------- player pool table ----------------------- */
  function positionMatchesFilter(eligible, pos) {
    if (pos === "ALL") return true;
    if (pos === "UTIL") return ["C", "1B", "2B", "3B", "SS", "OF"].some((p) => eligible.includes(p));
    return eligible.includes(pos);
  }

  /* ----------------------- upload / hydrate ----------------------- */
  async function onUploadFile(file) {
    if (!file) return;
    const text = await file.text();
    setCsvName(file.name || "");
    setParseError("");
    setStatusMsg("");

    try {
      const parsed = parseUploadedCsv(text, site);
      setUploadBundle(parsed);
      setSelectedContestName(parsed.selectedContestName || "");
      setSwappedLineups([]);
      setImportedLineups(parsed.lineups || []);
      setStatusMsg(
        parsed.kind === "contest"
          ? `Contest CSV loaded. ${parsed.contests.length} contest(s) found.`
          : `Lineups CSV loaded. ${parsed.lineups.length} lineup(s) found.`
      );
    } catch (e) {
      setUploadBundle(null);
      setImportedLineups([]);
      setSwappedLineups([]);
      setParseError(e?.message || "Could not parse uploaded CSV.");
    }
  }

  useEffect(() => {
    if (!uploadBundle) return;

    if (uploadBundle.kind === "contest") {
      const found =
        uploadBundle.contests.find((c) => c.name === selectedContestName) ||
        uploadBundle.contests[0] ||
        null;
      setImportedLineups(found?.lineups || []);
      if (found && found.name !== selectedContestName) {
        setSelectedContestName(found.name);
      }
    } else {
      setImportedLineups(uploadBundle.lineups || []);
      if (selectedContestName) setSelectedContestName("");
    }

    setSwappedLineups([]);
  }, [uploadBundle, selectedContestName, setImportedLineups, setSelectedContestName, setSwappedLineups]);

  function hydrateLineup(lineup) {
    const assignment = (lineup.assignment || []).map((x) => {
      const rawPlayerId = String(x.playerId || "").trim();
      const rawSlateCode = String(x.slateCode || "").trim();
      const rawName = String(x.name || "").trim();
      const rawOriginalCell = String(x.originalCell || "").trim();

      const parsedOriginal = parseExportCell(site, rawOriginalCell);

      const compositeId =
        rawSlateCode && rawPlayerId ? `${rawSlateCode}-${rawPlayerId}` : "";

      const parsedCompositeId =
        parsedOriginal.slateCode && parsedOriginal.playerId
          ? `${parsedOriginal.slateCode}-${parsedOriginal.playerId}`
          : "";

      const byCompositeId = compositeId ? rowByAlias.get(`id:${compositeId}`) : null;
      const byParsedCompositeId = parsedCompositeId
        ? rowByAlias.get(`id:${parsedCompositeId}`)
        : null;

      const byName = rawName ? rowByAlias.get(normName(rawName)) : null;
      const byParsedName = parsedOriginal.name
        ? rowByAlias.get(normName(parsedOriginal.name))
        : null;

      const row =
        byCompositeId ||
        byParsedCompositeId ||
        byName ||
        byParsedName ||
        null;

      const resolvedName = row?.name || parsedOriginal.name || rawName || "";
      const resolvedPlayerId =
        rawPlayerId ||
        parsedOriginal.playerId ||
        (slate === "early"
          ? row?.site_id_early
          : slate === "late"
          ? row?.site_id_late
          : row?.site_id_main) ||
        "";
      const resolvedSlateCode = rawSlateCode || parsedOriginal.slateCode || row?.slateCode || "";

      const lockedByGame = row?.game ? effectiveLockedGames.has(row.game) : false;
      const locked = !!parsedOriginal.lockedTag || lockedByGame;

      return {
        ...x,
        name: resolvedName,
        playerId: resolvedPlayerId,
        slateCode: resolvedSlateCode,
        row,
        game: row?.game || "",
        team: row?.team || "",
        opp: row?.opp || "",
        time: row?.time || "",
        salary: row?.salary || 0,
        proj: row?.eproj || 0,
        pown: row?.pown || 0,
        posDisp: Array.isArray(row?.eligible) ? row.eligible.join("/") : "",
        locked,
        isPitcher: !!row?.isPitcher,
      };
    });

    const lockedAssignment = assignment
      .filter((x) => x.locked && x.name)
      .map((x) => ({ slot: x.slot, name: x.name }));

    const openSlots = CFG.slots.filter(
      (s) => !lockedAssignment.some((x) => x.slot === s.name)
    );

    const lockedSalary = assignment
      .filter((x) => x.locked)
      .reduce((sum, x) => sum + (Number(x.salary) || 0), 0);

    const remainingSalary = CFG.cap - lockedSalary;
    const usedLocked = new Set(lockedAssignment.map((x) => x.name));

    const eligiblePlayers = rows.filter((r) => {
      if (usedLocked.has(r.name)) return false;
      if (r.game && effectiveLockedGames.has(r.game)) return false;
      if (effectiveExcls.has(r.name)) return false;
      if (hideUnprojected && r.siteIdOnly) return false;
      return true;
    });

    const hitters = assignment.filter((x) => x.name && !x.isPitcher);
    const byTeam = new Map();
    for (const h of hitters) {
      byTeam.set(h.team, (byTeam.get(h.team) || 0) + 1);
    }
    const sortedTeams = Array.from(byTeam.entries()).sort((a, b) => b[1] - a[1]);
    const originalPrimaryTeam = sortedTeams[0]?.[0] || "";
    const originalSecondaryTeam = sortedTeams[1]?.[0] || "";
    const originalPrimarySize = sortedTeams[0]?.[1] || 0;
    const originalSecondarySize = sortedTeams[1]?.[1] || 0;
    const originalStackShape = [originalPrimarySize, originalSecondarySize]
      .filter((x) => x > 0)
      .join("-") || "0-0";

    const lockedPitchers = assignment.filter((x) => x.locked && x.isPitcher && x.name);
    const lockedPitcherNames = lockedPitchers.map((x) => x.name);

    return {
      ...lineup,
      assignment,
      lockedAssignment,
      openSlots,
      lockedSalary,
      remainingSalary,
      eligiblePlayers,
      originalPrimaryTeam,
      originalSecondaryTeam,
      originalPrimarySize,
      originalSecondarySize,
      originalStackShape,
      lockedPitcherNames,
    };
  }

  const hydratedImported = useMemo(
    () => importedLineups.map((x) => hydrateLineup(x)),
    [importedLineups, rows, effectiveLockedGames, effectiveExcls, hideUnprojected, site, slate]
  );

  const usageSource = useMemo(
    () => (swappedLineups.length ? swappedLineups : hydratedImported),
    [swappedLineups, hydratedImported]
  );

  const lineupUsageByName = useMemo(() => {
    const counts = new Map();
    const source = swappedLineups.length ? swappedLineups : hydratedImported;

    for (const lineup of source) {
      const arr = swappedLineups.length
        ? lineup.finalAssignment || []
        : lineup.assignment || [];

      for (const a of arr) {
        const nm = String(a?.name || "").trim();
        if (!nm || nm === "—") continue;
        counts.set(nm, (counts.get(nm) || 0) + 1);
      }
    }

    return counts;
  }, [swappedLineups, hydratedImported]);

  const lineupUsagePctByName = useMemo(() => {
    const pctMap = new Map();
    const total = Math.max(swappedLineups.length || hydratedImported.length || 0, 1);
    for (const [name, count] of lineupUsageByName.entries()) {
      pctMap.set(name, (count / total) * 100);
    }
    return pctMap;
  }, [lineupUsageByName, swappedLineups.length, hydratedImported.length]);

  const availablePool = useMemo(() => {
    const lockedNamesFromCSV = new Set();
    const source = hydratedImported;

    for (const lineup of source) {
      for (const a of lineup.lockedAssignment || []) {
        if (a.name) lockedNamesFromCSV.add(a.name);
      }
    }

    const needle = String(q || "").trim().toLowerCase();

    let arr = rows
      .filter((r) => {
        if (r.game && effectiveLockedGames.has(r.game)) return false;
        if (lockedNamesFromCSV.has(r.name)) return false;
        // Keep manually excluded players visible in the pool so their Excl checkbox can be reviewed/toggled.
        // They are still excluded from the actual late-swap solver via effectiveExcls.
        if (hideUnprojected && r.siteIdOnly) return false;
        if (selectedGames.size && (!r.game || !selectedGames.has(r.game))) return false;
        if (!positionMatchesFilter(r.eligible || [], posFilter)) return false;

        if (!needle) return true;

        return (
          String(r.name || "").toLowerCase().includes(needle) ||
          String(r.team || "").toLowerCase().includes(needle) ||
          String(r.opp || "").toLowerCase().includes(needle) ||
          String((r.eligible || []).join("/")).toLowerCase().includes(needle) ||
          String(r.salary || "").includes(needle)
        );
      })
      .map((r) => ({
        ...r,
        usedPct: lineupUsagePctByName.get(r.name) || 0,
      }));

if (sort) {
  const dir = sort.dir === "asc" ? 1 : -1;
  arr = arr.slice().sort((a, b) => {
    let av = a[sort.col];
    let bv = b[sort.col];

    if (sort.col === "eligible") {
      av = String((a.eligible || []).join("/"));
      bv = String((b.eligible || []).join("/"));
    }
    if (sort.col === "battingOrder") {
      av = a.isPitcher ? 99 : Number(a.battingOrder || 99);
      bv = b.isPitcher ? 99 : Number(b.battingOrder || 99);
    }
    if (sort.col === "eproj") {
      av = a.eproj || 0;
      bv = b.eproj || 0;
    }
    if (sort.col === "val") {
      av = a.val || 0;
      bv = b.val || 0;
    }
    if (sort.col === "pown") {
      av = a.pown || 0;
      bv = b.pown || 0;
    }
    if (sort.col === "usedPct") {
      av = a.usedPct || 0;
      bv = b.usedPct || 0;
    }
    if (sort.col === "time") {
      av = parseMlbTimeToMinutes(a.time);
      bv = parseMlbTimeToMinutes(b.time);
      if (!Number.isFinite(av)) av = 1e9;
      if (!Number.isFinite(bv)) bv = 1e9;
    }

    if (typeof av === "string") av = av.toLowerCase();
    if (typeof bv === "string") bv = bv.toLowerCase();

    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return a.name.localeCompare(b.name);
  });
}

    return arr;
  }, [
  rows,
  hydratedImported,
  effectiveLockedGames,
  effectiveExcls,
  hideUnprojected,
  selectedGames,
  posFilter,
  q,
  sort,
  lineupUsagePctByName
]);
  
  /* ---------------------------- resetters ---------------------------- */
  function clearUpload() {
    setCsvName("");
    setUploadBundle(null);
    setImportedLineups([]);
    setSwappedLineups([]);
    setParseError("");
    setStatusMsg("");
    setSelectedContestName("");
    if (fileRef.current) fileRef.current.value = "";
  }

  function resetRules() {
    setLocks(new Set());
    setExcls(new Set());
    setBoost({});
    setTeamBoost({});
    setExcludedGames(new Set());
    setExcludedTeams(new Set());
    setSelectedGames(new Set());
    setMinExp({});
    setMaxExp({});
    setRandomness(0);
    setLineupPownMax("");
    setMaxPlayerPown("");
    setMaxFromTeam("");
    setAvoidHittersVsOppP(true);
    setMaxHittersVsOppP(0);
    setStackMode("preserve");
    setPreservePrimaryTeam(true);
    setPreserveSecondaryTeam(true);
    setPosFilter("ALL");
    setQ("");
  }

  function resetEverything() {
    resetRules();
    clearUpload();
    setLockedGames(new Set());
    setShowOnlyChanged(false);
    setUseAutoStarted(true);
    setHideUnprojected(true);
    setSort({ col: "eproj", dir: "desc" });
    setStatusMsg("");
    setParseError("");
  }

  /* ----------------------- export swapped csv ----------------------- */
  function buildExportRows() {
    const order = canonicalSlotOrder(site);

    if (!swappedLineups.length) return null;

    if (!uploadBundle || uploadBundle.kind === "lineups") {
      const rowsOut = [CFG.exportHeader];

      for (const lineup of swappedLineups) {
        const bySlot = {};
        for (const a of lineup.finalAssignment || []) bySlot[a.slot] = a;

    const row = order.map((slot) => {
      const item = bySlot[slot];
      if (!item?.name) return "";

      const playerRow = rowsByName.get(item.name);
      const originalItem = lineup.assignment.find((a) => a.slot === slot);
      const samePlayer =
        normName(originalItem?.name || "") === normName(item.name || "");

      const keepOriginal = !!item.locked || samePlayer;

      const { playerId, slateCode } = getExportParts({
        site,
        slate,
        row: playerRow,
        originalItem,
        samePlayer,
      });

      return formatExportCellPreserveOriginal(
        site,
        {
          name: item.name,
          playerId,
          slateCode,
        },
        idIndex,
        originalItem,
        keepOriginal
      );
    });

        rowsOut.push(row);
      }

      return rowsOut;
    }

    const outRows = (uploadBundle.rawRows || []).map((r) => [...r]);
    const slotIndexes = uploadBundle.slotIndexes || [];

    for (let i = 0; i < swappedLineups.length; i++) {
      const lineup = swappedLineups[i];
      const rowIdx = lineup.sourceRowIndex;
      if (rowIdx == null || !outRows[rowIdx]) continue;

      const bySlot = {};
      for (const a of lineup.finalAssignment || []) bySlot[a.slot] = a;

      order.forEach((slot, j) => {
        const cellIdx = slotIndexes[j];
        const item = bySlot[slot];

        if (!item?.name) {
          outRows[rowIdx][cellIdx] = "";
          return;
        }

        const row = rowsByName.get(item.name);
        const originalItem = lineup.assignment.find((a) => a.slot === slot);
        const samePlayer =
          normName(originalItem?.name || "") === normName(item.name || "");

        const keepOriginal = !!item.locked || samePlayer;

        const { playerId, slateCode } = getExportParts({
          site,
          slate,
          row,
          originalItem,
          samePlayer,
        });

        outRows[rowIdx][cellIdx] = formatExportCellPreserveOriginal(
          site,
          {
            name: item.name,
            playerId,
            slateCode,
          },
          idIndex,
          originalItem,
          keepOriginal
        );
      });
    }

    return outRows;
  }

  function exportSwappedCsv() {
    const rowsOut = buildExportRows();
    if (!rowsOut?.length) return;

    const csv = toCsv(rowsOut);
    const baseName = csvName
      ? csvName.replace(/\.csv$/i, "")
      : `mlb_${site}_late_swap_${new Date().toISOString().slice(0, 10)}`;

    const suffix =
      uploadBundle?.kind === "contest" && selectedContestName
        ? `_${selectedContestName.replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "")}_swapped`
        : "_swapped";

    download(`${baseName}${suffix}.csv`, csv);
  }

  /* -------------------------- late swap run -------------------------- */
  function buildLateSwapPayload({ lineup, eligiblePlayers, dynamicLocks, dynamicExcludes, dynamicBoosts }) {
    const openSlots = lineup.openSlots.map((s) => s.name);

    const lockedHitters = lineup.assignment.filter((x) => x.locked && !x.isPitcher && x.name);
    const lockedPitchers = lineup.assignment.filter((x) => x.locked && x.isPitcher && x.name);

    const lockedTeams = {};
    for (const h of lockedHitters) {
      lockedTeams[h.team] = (lockedTeams[h.team] || 0) + 1;
    }

    // IMPORTANT FOR LATE SWAP:
    // The backend needs metadata for the already-locked players so it can correctly
    // evaluate full-lineup rules (distinct teams, game counts, team caps, pOWN, etc.).
    // They remain impossible to select into open slots because locked_assignment is
    // sent separately and the backend explicitly forces those names out of open slots.
    const lockedNames = new Set(
      lineup.lockedAssignment.map((x) => String(x?.name || "").trim()).filter(Boolean)
    );

    const lockedPlayerRows = rows.filter((r) => lockedNames.has(r.name));
    const backendPlayerMap = new Map();

    for (const p of eligiblePlayers || []) {
      if (p?.name) backendPlayerMap.set(p.name, p);
    }
    for (const p of lockedPlayerRows) {
      if (p?.name && !backendPlayerMap.has(p.name)) backendPlayerMap.set(p.name, p);
    }

    const backendPlayers = Array.from(backendPlayerMap.values());

const getLateSwapAllowedShapes = () => {
  const hitterSlots = 8;
  const maxPerTeam = site === "dk" ? 5 : 4;

  const clampShapeForSite = (shape) => {
    const parts = String(shape || "").split("-").map((x) => Number(x));
    let a = parts[0] ?? 0;
    let b = parts[1] ?? 0;

    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

    a = Math.floor(a);
    b = Math.floor(b);

    // "0-0" means no strict stack requirement
    if (a === 0 && b === 0) return "0-0";

    // Otherwise both sides must be real stacks
    if (a < 1 || b < 1) return null;

    // Keep canonical order, ex: 3-4 becomes 4-3
    if (a < b) [a, b] = [b, a];

    // FanDuel max team stack is 4, DraftKings max is 5
    a = Math.min(maxPerTeam, a);
    b = Math.min(maxPerTeam, b);

    // Both DK/FD have 8 hitter slots
    if (a + b > hitterSlots) return null;

    return `${a}-${b}`;
  };

  const originalShape = clampShapeForSite(lineup.originalStackShape);

  const fallbackShapes =
    site === "dk"
      ? ["5-3", "5-2", "4-4", "4-3", "4-2", "3-3", "3-2", "2-2"]
      : ["4-4", "4-3", "4-2", "3-3", "3-2", "2-2"];

  return Array.from(
    new Set(
      [
        originalShape,
        ...fallbackShapes,
      ].filter(Boolean)
    )
  );
};

    return {
      site,
      cap_remaining: lineup.remainingSalary,
      open_slots: openSlots,
      locked_assignment: lineup.lockedAssignment.map((x) => ({ slot: x.slot, name: x.name })),
      players: backendPlayers.map((p) => ({
        name: p.name,
        team: p.team,
        opp: p.opp,
        eligible: p.eligible,
        salary: p.salary,
        proj: p.eproj,
        floor: p.floor || 0,
        ceil: p.ceil || 0,
        pown: p.pown || 0,
        is_pitcher: !!p.isPitcher,
        opp_pitcher: p.oppPitcher || "",
      })),
      objective: "proj",
      boosts: dynamicBoosts,
      randomness: Number(randomness) || 0,
      excludes: Array.from(dynamicExcludes || []),
      locks: Array.from(dynamicLocks || []),
      max_from_team:
        String(maxFromTeam).trim() === "" ? null : Math.max(1, Number(maxFromTeam) || 1),
      lineup_pown_max:
        String(lineupPownMax).trim() === "" ? null : Number(lineupPownMax),
      max_player_pown:
        String(maxPlayerPown).trim() === "" ? null : Number(maxPlayerPown),
      avoid_hitters_vs_opp_pitcher: !!avoidHittersVsOppP,
      max_hitters_vs_opp_pitcher: Math.max(0, Number(maxHittersVsOppP) || 0),

      stack_mode: stackMode,
      preserve_primary_team: !!preservePrimaryTeam,
      preserve_secondary_team: !!preserveSecondaryTeam,
      original_primary_team: lineup.originalPrimaryTeam || "",
      original_secondary_team: lineup.originalSecondaryTeam || "",
      original_primary_size: lineup.originalPrimarySize || 0,
      original_secondary_size: lineup.originalSecondarySize || 0,
      original_stack_shape: lineup.originalStackShape || "0-0",
      allowed_stack_shapes: stackMode === "preserve" ? getLateSwapAllowedShapes() : [],
      preserve_stack_shape: stackMode === "preserve",
      locked_team_counts: lockedTeams,
      locked_pitchers: lockedPitchers.map((x) => x.name),
      original_lineup: lineup.assignment.map((x) => ({ slot: x.slot, name: x.name })),
    };
  }

  async function runLateSwap() {
    if (!hydratedImported.length) return;

    setRunning(true);
    setStatusMsg("Running backend late swap...");
    setSwappedLineups([]);

    try {
      const out = [];
      const totalLineups = hydratedImported.length;
      const exposureCounts = new Map();

      const getMaxCount = (name) => {
        const raw = maxExp?.[name];
        if (raw == null || raw === "") return null;
        return Math.floor((Number(raw) / 100) * totalLineups + 1e-9);
      };

      const getMinCount = (name) => {
        const raw = minExp?.[name];
        if (raw == null || raw === "") return null;
        return Math.ceil((Number(raw) / 100) * totalLineups - 1e-9);
      };

      for (let i = 0; i < hydratedImported.length; i++) {
        const lineup = hydratedImported[i];
        setStatusMsg(`Running backend late swap... ${i + 1}/${hydratedImported.length}`);

        const originalOrdered = canonicalSlotOrder(site).map((slot) => {
          const found = lineup.assignment.find((x) => x.slot === slot);
          return { slot, name: found?.name || "" };
        });

        if (!lineup.openSlots.length) {
          const finalHydrated = originalOrdered.map((x) => {
            const row = x.name ? rowByAlias.get(normName(x.name)) : null;
            const originalItem = lineup.assignment.find((a) => a.slot === x.slot);
            const samePlayer =
              normName(originalItem?.name || "") === normName(x.name || "");
            const locked = !!lineup.lockedAssignment.find(
              (k) => k.slot === x.slot && k.name === x.name
            );

          const { playerId: exportPlayerId, slateCode: exportSlateCode } = getExportParts({
            site,
            slate,
            row,
            originalItem,
            samePlayer,
          });

            return {
              ...x,
              locked,
              row,
              playerId: exportPlayerId,
              slateCode: exportSlateCode,
              salary: row?.salary || 0,
              proj: row?.eproj || 0,
              pown: row?.pown || 0,
              team: row?.team || "",
              posDisp: row?.eligible?.join("/") || "",
              isPitcher: !!row?.isPitcher,
            };
          });

          for (const a of finalHydrated) {
            if (a.name) exposureCounts.set(a.name, (exposureCounts.get(a.name) || 0) + 1);
          }

          out.push({
            ...lineup,
            finalAssignment: finalHydrated,
            finalSalary: finalHydrated.reduce((s, x) => s + (x.salary || 0), 0),
            finalProj: finalHydrated.reduce((s, x) => s + (x.proj || 0), 0),
            finalPown: finalHydrated.reduce((s, x) => s + ((x.pown || 0) * 100), 0),
            changed: false,
            backendError: "",
          });

          continue;
        }

        try {
          const dynamicExcludes = new Set(excls);
          const dynamicLocks = new Set(locks);
          const dynamicBoosts = { ...boost };

          const remainingIncludingCurrent = totalLineups - i;

          for (const r of rows) {
            const maxCt = getMaxCount(r.name);
            if (maxCt != null) {
              const used = exposureCounts.get(r.name) || 0;
              if (used >= maxCt) dynamicExcludes.add(r.name);
            }
          }

          for (const r of rows) {
            const minCt = getMinCount(r.name);
            if (minCt == null || minCt <= 0) continue;

            const used = exposureCounts.get(r.name) || 0;
            const need = minCt - used;
            if (need <= 0) continue;
            if (dynamicExcludes.has(r.name)) continue;

            const mustNow = need >= remainingIncludingCurrent;
            if (mustNow) {
              dynamicLocks.add(r.name);
            } else {
              const urgency = need / remainingIncludingCurrent;
              const extra = Math.max(1, Math.min(9, Math.ceil(urgency * 9)));
              dynamicBoosts[r.name] = clamp((dynamicBoosts[r.name] || 0) + extra, -9, 9);
            }
          }

          const lockedNamesInLineup = new Set(lineup.lockedAssignment.map((x) => x.name));

          const eligiblePlayers = rows.filter((r) => {
            if (lockedNamesInLineup.has(r.name)) return false;
            if (r.game && effectiveLockedGames.has(r.game)) return false;
            if (dynamicExcludes.has(r.name)) return false;
            if (effectiveExcls.has(r.name)) return false;
            if (selectedGames.size && (!r.game || !selectedGames.has(r.game))) return false;

            if (avoidHittersVsOppP && !r.isPitcher) {
              const lockedPitcherTeams = lineup.assignment
                .filter((x) => x.locked && x.isPitcher && x.team)
                .map((x) => x.team);

              if (lockedPitcherTeams.includes(r.opp)) return false;
            }

            return true;
          });

          const payload = buildLateSwapPayload({
            lineup,
            eligiblePlayers,
            dynamicLocks,
            dynamicExcludes,
            dynamicBoosts,
          });

          const result = await runLateSwapBackend(payload);
          const normalized = normalizeBackendAssignment(site, result.assignment || []);
          const changed = JSON.stringify(originalOrdered) !== JSON.stringify(normalized);

          const finalHydrated = normalized.map((x) => {
            const row = x.name ? rowByAlias.get(normName(x.name)) : null;
            const originalItem = lineup.assignment.find((a) => a.slot === x.slot);
            const samePlayer =
              normName(originalItem?.name || "") === normName(x.name || "");
            const locked = !!lineup.lockedAssignment.find(
              (k) => k.slot === x.slot && k.name === x.name
            );

        const { playerId: exportPlayerId, slateCode: exportSlateCode } = getExportParts({
          site,
          slate,
          row,
          originalItem,
          samePlayer,
        });

            return {
              ...x,
              locked,
              row,
              playerId: exportPlayerId,
              slateCode: exportSlateCode,
              salary: row?.salary || 0,
              proj: row?.eproj || 0,
              pown: row?.pown || 0,
              team: row?.team || "",
              posDisp: row?.eligible?.join("/") || "",
              isPitcher: !!row?.isPitcher,
            };
          });

          for (const a of finalHydrated) {
            if (a.name) exposureCounts.set(a.name, (exposureCounts.get(a.name) || 0) + 1);
          }

          out.push({
            ...lineup,
            finalAssignment: finalHydrated,
            finalSalary: finalHydrated.reduce((s, x) => s + (x.salary || 0), 0),
            finalProj: finalHydrated.reduce((s, x) => s + (x.proj || 0), 0),
            finalPown: finalHydrated.reduce((s, x) => s + ((x.pown || 0) * 100), 0),
            changed,
            backendError: "",
          });
        } catch (err) {
          const fallbackHydrated = originalOrdered.map((x) => {
          const row = x.name ? rowByAlias.get(normName(x.name)) : null;
          const originalItem = lineup.assignment.find((a) => a.slot === x.slot);
          const samePlayer =
            normName(originalItem?.name || "") === normName(x.name || "");
          const locked = !!lineup.lockedAssignment.find(
            (k) => k.slot === x.slot && k.name === x.name
          );

          const { playerId: exportPlayerId, slateCode: exportSlateCode } = getExportParts({
            site,
            slate,
            row,
            originalItem,
            samePlayer,
          });

          return {
            ...x,
            locked,
            row,
            playerId: exportPlayerId,
            slateCode: exportSlateCode,
            salary: row?.salary || 0,
            proj: row?.eproj || 0,
            pown: row?.pown || 0,
            team: row?.team || "",
            posDisp: row?.eligible?.join("/") || "",
            isPitcher: !!row?.isPitcher,
          };
        });

          for (const a of fallbackHydrated) {
            if (a.name) exposureCounts.set(a.name, (exposureCounts.get(a.name) || 0) + 1);
          }

          out.push({
            ...lineup,
            finalAssignment: fallbackHydrated,
            finalSalary: fallbackHydrated.reduce((s, x) => s + (x.salary || 0), 0),
            finalProj: fallbackHydrated.reduce((s, x) => s + (x.proj || 0), 0),
            finalPown: fallbackHydrated.reduce((s, x) => s + ((x.pown || 0) * 100), 0),
            changed: false,
            backendError: err?.message || "Late swap failed",
          });
        }
      }

      setSwappedLineups(out);

      const changedCt = out.filter((x) => x.changed).length;
      const errorCt = out.filter((x) => x.backendError).length;
      setStatusMsg(
        `Finished. ${changedCt} of ${out.length} lineups changed.${errorCt ? ` ${errorCt} lineup(s) failed.` : ""}`
      );

      const usage = buildUsageTables(out);
      const saved = {
        id: `${Date.now()}`,
        createdAt: new Date().toISOString(),
        site,
        slate,
        csvName,
        uploadKind: uploadBundle?.kind || "lineups",
        selectedContestName: uploadBundle?.kind === "contest" ? selectedContestName : "",
        lineupCount: out.length,
        changedCount: changedCt,
        errorCount: errorCt,
        results: out,
        usage,
      };

      setSavedBuilds((prev) => [saved, ...(prev || [])].slice(0, 12));
    } finally {
      setRunning(false);
    }
  }

  /* ------------------------------ derived ui ------------------------------ */
  const visibleSwapped = useMemo(() => {
    const base = swappedLineups.length
      ? swappedLineups
      : hydratedImported.map((x) => ({
          ...x,
          finalAssignment: x.assignment.map((a) => ({
            slot: a.slot,
            name: a.name,
            locked: a.locked,
            row: a.row,
            playerId:
              a.playerId ||
              (slate === "early"
                ? a.row?.site_id_early
                : slate === "late"
                ? a.row?.site_id_late
                : a.row?.site_id_main) ||
              "",
            slateCode: a.slateCode || a.row?.slateCode || "",
            salary: a.salary,
            proj: a.proj,
            pown: a.pown,
            team: a.team,
            posDisp: a.posDisp,
            isPitcher: a.isPitcher,
          })),
          finalSalary: x.assignment.reduce((s, a) => s + (a.salary || 0), 0),
          finalProj: x.assignment.reduce((s, a) => s + (a.proj || 0), 0),
          finalPown: x.assignment.reduce((s, a) => s + ((a.pown || 0) * 100), 0),
          changed: false,
          backendError: "",
        }));

    return showOnlyChanged ? base.filter((x) => x.changed) : base;
  }, [swappedLineups, hydratedImported, showOnlyChanged, slate]);

  const usageTables = useMemo(() => buildUsageTables(usageSource), [usageSource]);
  const playerUsageRows = usageTables.players;
  const stackUsageRows = usageTables.stacks;

  const contestOptions = uploadBundle?.kind === "contest" ? uploadBundle.contests || [] : [];
  const currentContestCount =
    uploadBundle?.kind === "contest"
      ? contestOptions.find((c) => c.name === selectedContestName)?.count || 0
      : 0;

  const th =
    "px-2 py-1.5 text-[11px] font-semibold text-center whitespace-nowrap align-middle select-none";

  const td =
    "px-2 py-1.5 text-[11px] text-center whitespace-nowrap align-middle";

  const zebraRow = (active = false) =>
    cx(
      "transition-colors",
      active
        ? isDark
          ? "bg-blue-600/25"
          : "bg-blue-100"
        : isDark
        ? "odd:bg-slate-950/70 even:bg-slate-900/70 hover:bg-slate-800/80"
        : "odd:bg-white even:bg-gray-50 hover:bg-blue-50/60"
    );

  const tableShell = (extra = "") =>
    cx(
      "rounded-xl overflow-hidden border",
      isDark
        ? "border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
        : "border-slate-200 bg-white shadow-sm",
      extra
    );

  const miniSectionTitle = (sub = "") =>
    cx(
      "px-3 py-2 border-b text-sm font-semibold flex items-center justify-between gap-2",
      isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50",
      sub
    );

  const stickyHeadBase = cx(
    "sticky top-0 z-20",
    isDark
      ? "bg-slate-950 text-slate-100 border-b border-slate-800"
      : "bg-slate-50 text-slate-900 border-b border-slate-200"
  );

  const stickyTh = (extra = "") => cx(th, stickyHeadBase, extra);

  const stickyThDark = (extra = "") =>
    cx(
      th,
      "sticky top-0 z-20",
      isDark
        ? "bg-slate-950 text-slate-100 border-b border-slate-800 shadow-[0_1px_0_0_rgba(15,23,42,1)]"
        : "bg-slate-50 text-slate-900 border-b border-slate-200",
      extra
    );

  const scrollWrapClass =
    "overflow-auto overscroll-contain [scrollbar-gutter:stable]";

  const fullTableClass = (extra = "") =>
    cx("min-w-full w-full border-separate border-spacing-0", extra);

  const wideTableClass = (extra = "") =>
    cx("min-w-full w-max border-separate border-spacing-0", extra);

  const visiblePool = availablePool.filter((r) => !hideExcluded || !effectiveExcls.has(r.name));

  const toggleSort = (col) =>
    setSort((s) =>
      s.col === col
        ? { col, dir: s.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "desc" }
    );

  return (
    <div
      className={cx("mlb-lateswap", T.page, "px-4 md:px-6 py-5")}
      style={{
        "--mlb-surface": isDark ? "rgba(2,6,23,0.65)" : "rgba(255,255,255,0.92)",
        "--mlb-surface2": isDark ? "rgba(15,23,42,0.85)" : "rgba(248,250,252,1)",
        "--mlb-border": isDark ? "rgba(51,65,85,0.9)" : "rgba(226,232,240,1)",
        "--mlb-text": isDark ? "rgb(241,245,249)" : "rgb(15,23,42)",
        "--mlb-muted": isDark ? "rgb(148,163,184)" : "rgb(71,85,105)",
        "--mlb-thead": isDark ? "#020617" : "#f1f5f9",
        "--mlb-rowHover": isDark ? "rgba(56,189,248,0.08)" : "rgba(14,165,233,0.06)",
      }}
    >
      <style>{`
        .mlb-lateswap { color: var(--mlb-text); }

        .mlb-lateswap .ls-card {
          background: var(--mlb-surface2);
          border-color: var(--mlb-border);
        }

        .mlb-lateswap table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .mlb-lateswap thead,
        .mlb-lateswap thead tr {
          background-color: var(--mlb-thead) !important;
          opacity: 1 !important;
        }

        .mlb-lateswap thead th {
          background-color: var(--mlb-thead) !important;
          background-image: linear-gradient(var(--mlb-thead), var(--mlb-thead)) !important;
          background-clip: padding-box !important;
          color: var(--mlb-text);
          border-bottom: 1px solid var(--mlb-border);
          opacity: 1 !important;
          isolation: isolate;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          box-shadow: inset 0 -1px 0 var(--mlb-border), 0 1px 0 var(--mlb-thead);
        }

        .mlb-lateswap tbody td {
          border-bottom: 1px solid var(--mlb-border);
        }

        .mlb-lateswap tbody tr:hover td {
          background: var(--mlb-rowHover);
        }

        .mlb-lateswap input,
        .mlb-lateswap select,
        .mlb-lateswap textarea {
          background: var(--mlb-surface);
          color: var(--mlb-text);
          border-color: var(--mlb-border);
        }

        .mlb-lateswap input::placeholder,
        .mlb-lateswap textarea::placeholder {
          color: var(--mlb-muted);
        }

        .mlb-lateswap ::-webkit-scrollbar {
          height: 10px;
          width: 10px;
        }

        .mlb-lateswap ::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,0.35);
          border-radius: 999px;
        }

        .mlb-lateswap ::-webkit-scrollbar-track {
          background: rgba(148,163,184,0.08);
        }
      `}</style>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3 lg:flex-nowrap">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className={cx("whitespace-nowrap text-2xl md:text-3xl font-extrabold tracking-tight", T.headerText)}>
            MLB — Late Swap
          </h1>

          <div className={cx("flex flex-wrap items-baseline gap-x-3 text-xs", T.subtext)}>
            <span className="italic">Updated: {updatedAtText}</span>
            <span>{rows.length} rows</span>
          </div>
        </div>

        <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto sm:shrink-0">
          <div
            className={cx(
              "inline-flex items-center rounded-full border p-1 backdrop-blur-md",
              isDark
                ? "border-slate-700/80 bg-slate-900/40"
                : "border-slate-300 bg-white/80"
            )}
          >
            <button
              type="button"
              className={cx(
                "px-3 py-1 text-[11px] font-medium rounded-full transition",
                isDark ? "bg-blue-600 text-white" : "text-slate-600"
              )}
              onClick={() => setTheme("dark")}
            >
              Dark
            </button>

            <button
              type="button"
              className={cx(
                "px-3 py-1 text-[11px] font-medium rounded-full transition",
                !isDark ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"
              )}
              onClick={() => setTheme("light")}
            >
              Light
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className={cx("text-xs", T.subtext)}>
              Click here if new slate →
            </span>
            <button
              type="button"
              onClick={resetEverything}
              className="h-8 px-4 rounded-md bg-red-600 text-white text-sm font-semibold shadow-sm transition hover:bg-red-700 active:bg-red-800"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      <section aria-label="Slate and site" className={cx(cardClass, "ls-card mb-3 p-3")}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:flex-nowrap">
              <div className={topSegWrap}>
                <button
                  type="button"
                  onClick={() => setActiveTab("optimizer")}
                  className={topSegBtn(activeTab === "optimizer")}
                >
                  Optimizer
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("lateSwap")}
                  className={topSegBtn(activeTab === "lateSwap")}
                >
                  Late Swap
                </button>
              </div>

              <div className={topSegWrap}>
                {["dk", "fd"].map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setSite(s);
                      setUploadBundle(null);
                      setImportedLineups([]);
                      setSwappedLineups([]);
                      setParseError("");
                      setCsvName("");
                      setStatusMsg("");
                      setSelectedContestName("");
                      setLocks(new Set());
                      setExcls(new Set());
                      setBoost({});
                      setMinExp({});
                      setMaxExp({});
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                    className={siteSegBtn(site === s)}
                  >
                    <img
                      src={SITES[s].logo}
                      alt={SITES[s].label}
                      className="w-4 h-4 object-contain"
                    />
                    <span>{SITES[s].label}</span>
                  </button>
                ))}
              </div>
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <div className={cx("flex h-9 items-center pb-2 text-xs font-medium", isDark ? "text-slate-200" : "text-slate-700")}>
                Slate:
              </div>

              <div className={slateSegWrap}>
                {[
                  { key: "main", label: "Main" },
                  { key: "early", label: "Early" },
                  { key: "late", label: "Late" },
                ].map((sl) => (
                  <button
                    key={sl.key}
                    type="button"
                    onClick={() => {
                      setSlate(sl.key);
                      setSwappedLineups([]);
                    }}
                    className={slateSegBtn(slate === sl.key)}
                  >
                    {sl.label}
                  </button>
                ))}
              </div>
            </div>
        </div>
      </section>
      <section aria-label="Late swap actions" className={cx(cardClass, "ls-card mb-3 p-3")}>
          <div className="flex items-center gap-2 flex-wrap">
            <label className={ghostBtnClass + " inline-flex items-center cursor-pointer"}>
              Upload CSV
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => onUploadFile(e.target.files?.[0])}
              />
            </label>

            <button
              className={ghostBtnClass}
              onClick={clearUpload}
              disabled={!importedLineups.length && !csvName}
            >
              Clear
            </button>

            <button
              className={primaryBtnClass}
              onClick={runLateSwap}
              disabled={!importedLineups.length || running}
            >
              {running ? "Running…" : "Run Late Swap"}
            </button>

            <button
              className={ghostBtnClass}
              onClick={exportSwappedCsv}
              disabled={!swappedLineups.length}
            >
              Export Swapped CSV
            </button>
          </div>
        <details className={cx("mt-3 border-t pt-3", isDark ? "border-slate-800" : "border-slate-200")}>
          <summary className="cursor-pointer select-none text-sm font-semibold">Reset Tools</summary>
          <div className="mt-3">
      <div className="flex flex-wrap gap-2">
        <button className={ghostBtnClass} onClick={() => setExcls(new Set())}>
          Clear Excludes
        </button>

        <button className={ghostBtnClass} onClick={() => setLocks(new Set())}>
          Clear Locks
        </button>

        <button className={ghostBtnClass} onClick={resetRules}>Reset All Rules</button>
        <button className={ghostBtnClass} onClick={() => setBoost({})}>Reset Boosts</button>
        <button className={ghostBtnClass} onClick={() => setLocks(new Set())}>Reset Locks</button>
        <button className={ghostBtnClass} onClick={() => setExcls(new Set())}>Reset Excludes</button>
        <button className={ghostBtnClass} onClick={() => { setMinExp({}); setMaxExp({}); }}>
          Reset Exposures
        </button>
        <button
          className={ghostBtnClass}
          onClick={() => {
            setRandomness(0);
            setLineupPownMax("");
            setMaxPlayerPown("");
            setMaxFromTeam("");
            setAvoidHittersVsOppP(true);
            setMaxHittersVsOppP(0);
            setStackMode("preserve");
            setPreservePrimaryTeam(true);
            setPreserveSecondaryTeam(true);
            setTeamBoost({});
            setExcludedGames(new Set());
            setExcludedTeams(new Set());
            setSelectedGames(new Set());
          }}
        >
          Reset Constraints
        </button>
        <button
          className={ghostBtnClass}
          onClick={() => {
            setPosFilter("ALL");
            setQ("");
          }}
        >
          Reset Filters
        </button>
      </div>

          </div>
        </details>
      </section>

      {uploadBundle?.kind === "contest" && (
        <div className={cx(cardClass, "mb-4 p-3 ls-card")}>
          <div className="text-sm font-semibold mb-2">Contest Selection</div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className={cx("block text-[10px] mb-1", T.subtext)}>Contest</label>

              <select
                className={inputClass + " w-full"}
                value={selectedContestName}
                onChange={(e) => {
                  setSelectedContestName(e.target.value);
                  setSwappedLineups([]);
                }}
              >
                {contestOptions.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.count})
                  </option>
                ))}
              </select>
            </div>

            <div className={cx("text-sm", T.subtext)}>
              <div>
                Contest lines: <span className="font-semibold">{currentContestCount}</span>
              </div>
              <div>
                Total contests in file:{" "}
                <span className="font-semibold">{contestOptions.length}</span>
              </div>
            </div>

            <div className={cx("text-sm", T.subtext)}>
              Export preserves every other contest and only replaces the selected contest’s lineup cells.
            </div>
          </div>
        </div>
      )}

      <div className={cx(cardClass, "mb-4 p-3 ls-card")}>
        <div className="text-sm font-semibold mb-3">Late Swap Settings</div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
          <div className="md:col-span-2">
            <label className={cx("block text-[10px] mb-1", T.subtext)}>Randomness %</label>
            <input className={inputClass + " w-full"} value={randomness} onChange={(e) => setRandomness(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className={cx("block text-[10px] mb-1", T.subtext)}>Max hitters from team</label>
            <input className={inputClass + " w-full"} value={maxFromTeam} onChange={(e) => setMaxFromTeam(e.target.value)} placeholder="—" />
          </div>

          <div className="md:col-span-2">
            <label className={cx("block text-[10px] mb-1", T.subtext)}>Max lineup pOWN% sum</label>
            <input className={inputClass + " w-full"} value={lineupPownMax} onChange={(e) => setLineupPownMax(e.target.value)} placeholder="—" />
          </div>

          <div className="md:col-span-2">
            <label className={cx("block text-[10px] mb-1", T.subtext)}>Max player pOWN%</label>
            <input className={inputClass + " w-full"} value={maxPlayerPown} onChange={(e) => setMaxPlayerPown(e.target.value)} placeholder="—" />
          </div>

          <div className="md:col-span-2">
            <label className={cx("block text-[10px] mb-1", T.subtext)}>Max hitters vs opp P</label>
            <input className={inputClass + " w-full"} value={maxHittersVsOppP} onChange={(e) => setMaxHittersVsOppP(e.target.value)} placeholder="0" />
          </div>

          <div className="md:col-span-3">
            <label className={cx("block text-[10px] mb-1", T.subtext)}>Stack handling</label>
            <select className={inputClass + " w-full"} value={stackMode} onChange={(e) => setStackMode(e.target.value)}>
              <option value="preserve">Preserve original stack shape</option>
              <option value="none">Ignore stacks</option>
            </select>
          </div>

          {[
            {
              label: "Avoid hitters vs opp P",
              description: "Blocks hitters against their opposing pitcher.",
              checked: avoidHittersVsOppP,
              onChange: setAvoidHittersVsOppP,
            },
            {
              label: "Preserve primary stack team",
              description: "Keeps the original lineup's main stack team.",
              checked: preservePrimaryTeam,
              onChange: setPreservePrimaryTeam,
            },
            {
              label: "Preserve secondary stack team",
              description: "Keeps the original lineup's secondary stack team.",
              checked: preserveSecondaryTeam,
              onChange: setPreserveSecondaryTeam,
            },
          ].map((option) => (
            <button
              key={option.label}
              type="button"
              role="switch"
              aria-checked={option.checked}
              onClick={() => option.onChange(!option.checked)}
              className={cx(
                "md:col-span-3 w-full min-h-[66px] rounded-xl border px-3 py-2.5",
                "flex items-center justify-between gap-3 text-left transition",
                option.checked
                  ? isDark
                    ? "border-sky-500/60 bg-sky-500/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
                    : "border-sky-400 bg-sky-50"
                  : isDark
                  ? "border-slate-800 bg-slate-950/35 hover:border-slate-700 hover:bg-slate-900/70"
                  : "border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-white"
              )}
            >
              <span className="min-w-0">
                <span
                  className={cx(
                    "block text-xs font-semibold",
                    option.checked ? "text-sky-400" : T.headerText
                  )}
                >
                  {option.label}
                </span>
                <span className={cx("mt-0.5 block text-[10px] leading-4", T.subtext)}>
                  {option.description}
                </span>
              </span>

              <span
                aria-hidden="true"
                className={cx(
                  "relative inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors",
                  option.checked
                    ? "border-sky-500 bg-sky-500"
                    : isDark
                    ? "border-slate-600 bg-slate-800"
                    : "border-slate-300 bg-slate-300"
                )}
              >
                <span
                  className={cx(
                    "absolute top-0.5 rounded-full bg-white shadow-sm transition-transform",
                    option.checked ? "translate-x-[21px]" : "translate-x-0.5"
                  )}
                  style={{ width: 18, height: 18 }}
                />
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className={cx(cardClass, "mb-4 p-3 ls-card")}>
        <div className="text-sm font-semibold mb-2">Upload / Status</div>
        <div className={cx("text-xs mb-1", T.subtext)}>File: {csvName || "—"}</div>
        <div className={cx("text-xs mb-1", T.subtext)}>
          CSV Type: {uploadBundle?.kind ? uploadBundle.kind.toUpperCase() : "—"}
        </div>
        <div className={cx("text-xs mb-1", T.subtext)}>Imported lineups: {importedLineups.length}</div>
        <div className={cx("text-xs mb-1", T.subtext)}>Current site: {CFG.label}</div>
        <div className={cx("text-xs mb-1", T.subtext)}>Current slate: {slate.toUpperCase()}</div>

        {uploadBundle?.kind === "contest" && (
          <div className={cx("text-xs mb-1", T.subtext)}>
            Selected contest: {selectedContestName || "—"}
          </div>
        )}

        <div className={cx("text-xs mb-2", T.subtext)}>
          Effective locked games: {effectiveLockedGames.size}
        </div>

        {statusMsg && (
          <div
            className={cx(
              "text-xs px-2 py-1 rounded-md border",
              isDark
                ? "bg-sky-500/10 text-sky-200 border-sky-500/25"
                : "bg-blue-50 text-blue-700 border-blue-200"
            )}
          >
            {statusMsg}
          </div>
        )}

        {parseError && (
          <div
            className={cx(
              "mt-2 text-xs px-2 py-1 rounded-md border",
              isDark
                ? "bg-red-500/10 text-red-200 border-red-500/25"
                : "bg-red-50 text-red-700 border-red-200"
            )}
          >
            {parseError}
          </div>
        )}
      </div>

      <div className={cx(cardClass, "mb-4 p-3 ls-card")}>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="text-sm font-semibold">Games</div>

          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={useAutoStarted} onChange={(e) => setUseAutoStarted(e.target.checked)} />
              Auto-lock started games
            </label>

            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={showOnlyChanged} onChange={(e) => setShowOnlyChanged(e.target.checked)} />
              Show only changed
            </label>

            <button className={ghostBtnClass} onClick={() => setSelectedGames(new Set(allGames.map((g) => g.key)))}>
              Select All Games
            </button>

            <button className={ghostBtnClass} onClick={() => setSelectedGames(new Set())}>
              Clear Game Selection
            </button>

            <button className={ghostBtnClass} onClick={() => setLockedGames(new Set(allGames.map((g) => g.key)))}>
              Lock All Games
            </button>

            <button className={ghostBtnClass} onClick={() => setLockedGames(new Set())}>
              Clear Manual Locks
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-1">
            {allGames.map((g) => {
              const active = selectedGames.has(g.key);
              const manual = lockedGames.has(g.key);
              const auto = autoLockedGames.has(g.key);
              const lockedLabel = manual ? "manual lock" : auto ? "auto lock" : "";

              const away = g.a;
              const home = g.b;

              const awayTotal = teamTotalByTeam.get(away);
              const homeTotal = teamTotalByTeam.get(home);

              const awayExcluded = excludedTeams.has(away);
              const homeExcluded = excludedTeams.has(home);

              const teamLogoSrc = (abbr) =>
                abbr ? `/logos/mlb/${String(abbr).toLowerCase()}.png` : "";

              return (
                <div
                  key={g.key}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    setSelectedGames((S) => {
                      const n = new Set(S);
                      active ? n.delete(g.key) : n.add(g.key);
                      return n;
                    })
                  }
                  className={cx(
                    "w-[150px] shrink-0 rounded-xl border overflow-hidden transition-all duration-150 cursor-pointer",
                    manual || auto
                      ? isDark
                        ? "border-red-700/60 bg-red-950/10"
                        : "border-red-300 bg-red-50"
                      : active
                      ? isDark
                        ? "border-sky-500/60 bg-slate-900/95"
                        : "border-sky-300 bg-white shadow-sm"
                      : isDark
                      ? "border-slate-800 bg-slate-900/90 hover:border-slate-700"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  )}
                >
                  <div
                    className={cx(
                      "px-2 py-1.5 border-b",
                      manual || auto
                        ? isDark
                          ? "border-red-800/40 bg-red-950/20"
                          : "border-red-200 bg-red-50"
                        : isDark
                        ? "border-slate-800 bg-slate-800/40"
                        : "border-slate-200 bg-slate-50"
                    )}
                  >
                    <div className="grid grid-cols-[16px_1fr_20px] items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedGames((S) => {
                            const n = new Set(S);
                            active ? n.delete(g.key) : n.add(g.key);
                            return n;
                          });
                        }}
                        className={cx(
                          "w-4 h-4 rounded-[4px] border flex items-center justify-center text-[9px] leading-none",
                          active
                            ? isDark
                              ? "border-sky-400 bg-sky-500/15 text-sky-300"
                              : "border-sky-500 bg-sky-50 text-sky-700"
                            : isDark
                            ? "border-slate-700 text-slate-500"
                            : "border-slate-300 text-slate-400"
                        )}
                      >
                        {active ? "✓" : ""}
                      </button>

                      <div className="text-center">
                        <div className={cx("text-[11px] font-semibold whitespace-nowrap", isDark ? "text-slate-300" : "text-slate-600")}>
                          {g.displayTime || "—"}
                        </div>

                        <div
                          className={cx(
                            "mt-1 text-center text-[10px] font-semibold",
                            manual
                              ? isDark
                                ? "text-red-300"
                                : "text-red-700"
                              : auto
                              ? isDark
                                ? "text-slate-400"
                                : "text-slate-500"
                              : "text-transparent"
                          )}
                        >
                          {lockedLabel || "placeholder"}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLockedGames((prev) => {
                            const next = new Set(prev);
                            if (next.has(g.key)) next.delete(g.key);
                            else next.add(g.key);
                            return next;
                          });
                        }}
                        className={cx(
                          "w-5 h-5 rounded-md border flex items-center justify-center text-[11px] leading-none",
                          manual
                            ? "bg-red-600 text-white border-red-700"
                            : isDark
                            ? "border-slate-700 text-slate-400 hover:bg-slate-800"
                            : "border-slate-300 text-slate-500 hover:bg-slate-100"
                        )}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="px-2 py-2">
                    {[
                      { abbr: away, total: awayTotal, teamExcluded: awayExcluded },
                      { abbr: home, total: homeTotal, teamExcluded: homeExcluded },
                    ].map(({ abbr, total, teamExcluded }, idx) => (
                      <div
                        key={`${g.key}-${abbr}-${idx}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExcludeTeam(abbr);
                        }}
                        className={cx(
                          "flex items-center justify-between rounded-lg px-2 py-1.5",
                          idx === 0 ? "mb-1.5" : "",
                          teamExcluded
                            ? isDark
                              ? "bg-red-950/20 ring-1 ring-red-800/40"
                              : "bg-red-50 ring-1 ring-red-200"
                            : isDark
                            ? "bg-slate-800/45"
                            : "bg-slate-50"
                        )}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <img
                            src={teamLogoSrc(abbr)}
                            alt={abbr}
                            className="w-5 h-5 object-contain shrink-0"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />

                          <span className={cx("font-semibold text-[11px]", isDark ? "text-slate-100" : "text-slate-900")}>
                            {abbr}
                          </span>
                        </div>

                        <div className="ml-auto flex items-center gap-2 shrink-0">
                          <div className="flex flex-col items-center justify-center leading-none -space-y-[1px]">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                bumpTeamBoost(abbr, +1);
                              }}
                              className="h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition"
                              title={`Boost ${abbr} hitters +10%`}
                            >
                              ▲
                            </button>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                bumpTeamBoost(abbr, -1);
                              }}
                              className="h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition"
                              title={`Reduce ${abbr} hitters -10%`}
                            >
                              ▼
                            </button>
                          </div>

                          <div className="min-w-[34px] text-right leading-none">
                            <div className={cx("text-[14px] font-semibold tabular-nums", isDark ? "text-slate-200" : "text-slate-700")}>
                              {total ? fmt1(total) : "—"}
                            </div>

                            {!!(teamBoost[abbr] || 0) && (
                              <div
                                className={cx(
                                  "text-[9px] tabular-nums mt-0.5",
                                  (teamBoost[abbr] || 0) > 0 ? "text-emerald-500" : "text-rose-500"
                                )}
                              >
                                {(teamBoost[abbr] || 0) > 0 ? `+${teamBoost[abbr]}` : teamBoost[abbr]}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className={tableShell("mb-4 ls-card")}>
        <div className={miniSectionTitle()}>
          <h2 className="text-sm font-semibold">Player Pool</h2>
          <span className={cx("text-xs font-normal", T.subtext)}>{visiblePool.length} players shown</span>
        </div>
        <div className="px-3 pt-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {["ALL", "P", "C", "1B", "2B", "3B", "SS", "OF", "UTIL"].map((p) => (
          <button
            key={p}
            onClick={() => setPosFilter(p)}
            className={`relative pb-2 text-xs font-semibold transition-colors ${posFilter === p
              ? "text-sky-500 after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-sky-400"
              : isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-800"}`}
          >
            {p}
          </button>
        ))}

        <input
          className={inputClass + " w-full sm:ml-auto sm:max-w-xs"}
          placeholder="Search player / team / opp / pos / salary"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className={cx(ghostBtnClass, "inline-flex cursor-pointer items-center gap-2 text-xs")}>
          <input type="checkbox" className="h-3.5 w-3.5 accent-sky-500" checked={hideUnprojected} onChange={() => setHideUnprojected((v) => !v)} />
          Hide Unprojected
        </label>
        <label className={cx(ghostBtnClass, "inline-flex cursor-pointer items-center gap-2 text-xs")}>
          <input type="checkbox" className="h-3.5 w-3.5 accent-sky-500" checked={hideExcluded} onChange={(e) => setHideExcluded(e.target.checked)} />
          Hide Excluded
        </label>

        <button
          className={ghostBtnClass}
          onClick={() => {
            setExcls((prev) => {
              const next = new Set(prev);
              const names = availablePool.map((r) => r.name);
              const allExcluded = names.length > 0 && names.every((n) => next.has(n));
              if (allExcluded) names.forEach((n) => next.delete(n));
              else names.forEach((n) => next.add(n));
              return next;
            });
          }}
        >
          Exclude all shown
        </button>

        <button
          className={ghostBtnClass}
          onClick={() => {
            if (posFilter === "ALL") return;
            const names = rows
              .filter((r) => positionMatchesFilter(r.eligible || [], posFilter))
              .map((r) => r.name);

            setExcls((prev) => {
              const next = new Set(prev);
              const allExcluded = names.length > 0 && names.every((n) => next.has(n));
              if (allExcluded) names.forEach((n) => next.delete(n));
              else names.forEach((n) => next.add(n));
              return next;
            });
          }}
        >
          Exclude position
        </button>

      </div>

        </div>

        <div className={scrollWrapClass} style={{ maxHeight: 520 }}>
          <table className={cx(wideTableClass("table-auto text-xs"), isDark ? "text-slate-100" : "text-slate-900")}>
            <thead>
              <tr>
                <th className={stickyTh()}>Lock</th>
                <th className={stickyTh()}>Excl</th>
                <th className={stickyTh()} onClick={() => toggleSort("eligible")} style={{ cursor: "pointer" }}>Pos</th>
                <th className={stickyTh()} onClick={() => toggleSort("battingOrder")} style={{ cursor: "pointer" }}>BO</th>
                <th className={stickyTh()}>Boost</th>
                <th className={stickyTh("text-left")} onClick={() => toggleSort("name")} style={{ cursor: "pointer" }}>Player</th>
                <th className={stickyTh()} onClick={() => toggleSort("team")} style={{ cursor: "pointer" }}>Tm</th>
                <th className={stickyTh()} onClick={() => toggleSort("opp")} style={{ cursor: "pointer" }}>Opp</th>
                <th className={stickyTh()} onClick={() => toggleSort("salary")} style={{ cursor: "pointer" }}>Salary</th>
                <th className={stickyTh()} onClick={() => toggleSort("time")} style={{ cursor: "pointer" }}>Time</th>
                <th className={stickyTh()} onClick={() => toggleSort("eproj")} style={{ cursor: "pointer" }}>Proj</th>
                <th className={stickyTh()} onClick={() => toggleSort("val")} style={{ cursor: "pointer" }}>Val</th>
                <th className={stickyTh()} onClick={() => toggleSort("pown")} style={{ cursor: "pointer" }}>pOWN%</th>
                <th className={stickyTh()} onClick={() => toggleSort("usedPct")} style={{ cursor: "pointer" }}>Used %</th>
                <th className={stickyTh()}>Min%</th>
                <th className={stickyTh()}>Max%</th>
              </tr>
            </thead>

            <tbody>
              {visiblePool.map((r) => {
                const locked = locks.has(r.name);
                const excluded = excls.has(r.name);

                return (
                  <tr key={r.name} className={zebraRow()}>
                    <td className={td}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-blue-500 cursor-pointer"
                        checked={locked}
                        onChange={() =>
                          setLocks((prev) => {
                            const next = new Set(prev);
                            if (next.has(r.name)) next.delete(r.name);
                            else next.add(r.name);
                            return next;
                          })
                        }
                      />
                    </td>

                    <td className={td}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-blue-500 cursor-pointer"
                        checked={excluded}
                        onChange={() =>
                          setExcls((prev) => {
                            const next = new Set(prev);
                            if (next.has(r.name)) next.delete(r.name);
                            else next.add(r.name);
                            return next;
                          })
                        }
                      />
                    </td>

                    <td className={td}>{(r.eligible || []).join("/")}</td>
                    <td className={td}>{r.isPitcher ? "-" : (r.battingOrder || "-")}</td>

                    <td className={td}>
                      <div className="inline-flex items-center gap-1">
                        <button
                          className={cx(
                            "w-6 h-6 rounded-md border px-0",
                            isDark
                              ? "border-slate-700 bg-slate-900/60 text-slate-100 hover:bg-slate-800"
                              : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
                          )}
                          onClick={() =>
                            setBoost((b) => ({
                              ...b,
                              [r.name]: clamp((b[r.name] || 0) - 1, -9, 9),
                            }))
                          }
                        >
                          −
                        </button>

                        <input
                          className={cx(
                            "h-6 w-8 px-1 text-center text-[11px] rounded-md border",
                            isDark
                              ? "bg-slate-950/70 border-slate-700 text-slate-100"
                              : "bg-white border-slate-300 text-slate-800"
                          )}
                          value={r.boostSteps ?? 0}
                          onChange={(e) =>
                            setBoost((b) => ({
                              ...b,
                              [r.name]: clamp(Math.round(+e.target.value || 0), -9, 9),
                            }))
                          }
                        />

                        <button
                          className={cx(
                            "w-6 h-6 rounded-md border px-0",
                            isDark
                              ? "border-slate-700 bg-slate-900/60 text-slate-100 hover:bg-slate-800"
                              : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
                          )}
                          onClick={() =>
                            setBoost((b) => ({
                              ...b,
                              [r.name]: clamp((b[r.name] || 0) + 1, -9, 9),
                            }))
                          }
                        >
                          +
                        </button>
                      </div>
                    </td>

                    <td className={td + " text-left"}>
                      <div className="truncate max-w-[170px]" title={r.siteIdOnly ? `${r.name} — unprojected/site pool` : r.name}>
                        <span>{r.name}</span>
                        {r.siteIdOnly && (
                          <span className={cx("ml-1 text-[10px]", T.subtext)} title="Loaded from site_ids.json">ID</span>
                        )}
                      </div>
                    </td>

                    <td className={td}>
                      <span style={chipStyle(r.team)}>{r.team || "—"}</span>
                    </td>

                    <td className={td}>
                      <span style={chipStyle(r.opp)}>{r.opp || "—"}</span>
                    </td>

                    <td className={td}>{fmt0(r.salary)}</td>
                    <td className={td}>{formatMlbTimeET(r.time) || ""}</td>
                    <td className={td}>{fmt1(r.eproj || 0)}</td>
                    <td className={td}>{fmt1(r.val || 0)}</td>
                    <td className={td}>{fmt1((r.pown || 0) * 100)}%</td>
                    <td className={td}>{fmt1(r.usedPct || 0)}%</td>

                    <td className={td}>
                      <input
                        className={cx(
                          "h-6 w-8 px-1 text-center text-[11px] rounded-md border",
                          isDark
                            ? "bg-slate-950/70 border-slate-700 text-slate-100"
                            : "bg-white border-slate-300 text-slate-800"
                        )}
                        value={minExp[r.name] ?? ""}
                        onChange={(e) =>
                          setMinExp((prev) => ({
                            ...prev,
                            [r.name]: e.target.value === "" ? "" : clamp(Number(e.target.value) || 0, 0, 100),
                          }))
                        }
                      />
                    </td>

                    <td className={td}>
                      <input
                        className={cx(
                          "h-6 w-8 px-1 text-center text-[11px] rounded-md border",
                          isDark
                            ? "bg-slate-950/70 border-slate-700 text-slate-100"
                            : "bg-white border-slate-300 text-slate-800"
                        )}
                        value={maxExp[r.name] ?? ""}
                        onChange={(e) =>
                          setMaxExp((prev) => ({
                            ...prev,
                            [r.name]: e.target.value === "" ? "" : clamp(Number(e.target.value) || 0, 0, 100),
                          }))
                        }
                      />
                    </td>
                  </tr>
                );
              })}

              {!visiblePool.length && (
                <tr className={zebraRow()}>
                  <td className={td} colSpan={16}>
                    No available late-swap players for the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <div className={tableShell("ls-card")}>
          <div className={miniSectionTitle()}>Stack Exposure</div>

          <div className={scrollWrapClass} style={{ maxHeight: 420 }}>
            <table className={fullTableClass("text-xs")}>
              <thead>
                <tr>
                  <th className={stickyTh("text-left")}>Team</th>
                  <th className={stickyTh()}>Count</th>
                  <th className={stickyTh()}>Stack %</th>
                  <th className={stickyTh()}>Avg Stack</th>
                </tr>
              </thead>

              <tbody>
                {stackUsageRows.map((r) => (
                  <tr key={r.team} className={zebraRow()}>
                    <td className={td + " text-left"}>
                      <span style={chipStyle(r.team)}>{r.team}</span>
                    </td>
                    <td className={td}>{r.count}</td>
                    <td className={td}>{fmt1(r.exposure)}%</td>
                    <td className={td}>{fmt1(r.avgStackSize)}</td>
                  </tr>
                ))}

                {!stackUsageRows.length && (
                  <tr className={zebraRow()}>
                    <td className={td} colSpan={4}>
                      Run or load a build to see stack exposure.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={tableShell("ls-card")}>
          <div className={miniSectionTitle()}>Player Usage</div>

          <div className={scrollWrapClass} style={{ maxHeight: 420 }}>
            <table className={fullTableClass("text-xs")}>
              <thead>
                <tr>
                  <th className={stickyTh("text-left")}>Player</th>
                  <th className={stickyTh()}>Pos</th>
                  <th className={stickyTh()}>Tm</th>
                  <th className={stickyTh()}>Count</th>
                  <th className={stickyTh()}>Exposure %</th>
                </tr>
              </thead>

              <tbody>
                {playerUsageRows.map((r) => (
                  <tr key={r.name} className={zebraRow()}>
                    <td className={td + " text-left"}>{r.name}</td>
                    <td className={td}>{r.pos || ""}</td>
                    <td className={td}>
                      <span style={chipStyle(r.team)}>{r.team || "—"}</span>
                    </td>
                    <td className={td}>{r.count}</td>
                    <td className={td}>{fmt1(r.exposure)}%</td>
                  </tr>
                ))}

                {!playerUsageRows.length && (
                  <tr className={zebraRow()}>
                    <td className={td} colSpan={5}>
                      Run or load a build to see usage.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className={tableShell("mb-4 ls-card")}>
        <div className={miniSectionTitle()}>Imported Lineups</div>

        <div className={scrollWrapClass} style={{ maxHeight: 360 }}>
          <table className={fullTableClass("min-w-[1180px] text-xs")}>
            <thead>
              <tr>
                <th className={stickyTh()}>#</th>
                {uploadBundle?.kind === "contest" && <th className={stickyTh()}>Entry ID</th>}
                {uploadBundle?.kind === "contest" && <th className={stickyTh("text-left")}>Contest</th>}
                <th className={stickyTh()}>Locked Slots</th>
                <th className={stickyTh()}>Open Slots</th>
                <th className={stickyTh()}>Locked Salary</th>
                <th className={stickyTh()}>Remaining Salary</th>
                <th className={stickyTh()}>Orig Stack</th>
                <th className={stickyTh("text-left")}>Players</th>
              </tr>
            </thead>

            <tbody>
              {hydratedImported.map((lineup) => (
                <tr key={lineup.id} className={zebraRow()}>
                  <td className={td}>{lineup.index}</td>

                  {uploadBundle?.kind === "contest" && (
                    <td className={td}>{lineup.entryId || "—"}</td>
                  )}

                  {uploadBundle?.kind === "contest" && (
                    <td className={td + " text-left"}>{lineup.contestName || "—"}</td>
                  )}

                  <td className={td}>{lineup.lockedAssignment.length}</td>
                  <td className={td}>{lineup.openSlots.length}</td>
                  <td className={td}>{fmt0(lineup.lockedSalary)}</td>
                  <td className={td}>{fmt0(lineup.remainingSalary)}</td>
                  <td className={td}>{lineup.originalStackShape}</td>

                  <td className={td + " text-left"}>
                    <div className="flex flex-wrap gap-1">
                      {lineup.assignment.map((a) => (
                        <span
                          key={`${lineup.id}:${a.slot}`}
                          className={
                            "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 " +
                            (a.locked
                              ? isDark
                                ? "bg-rose-500/10 border-rose-400/40 text-rose-200"
                                : "bg-red-50 border-red-300 text-red-700"
                              : isDark
                              ? "bg-sky-900/20 border-sky-800 text-sky-200"
                              : "bg-blue-50 border-blue-200 text-blue-700")
                          }
                        >
                          <span className="font-semibold">{a.slot}</span>
                          <span>{a.name || "—"}</span>
                          {a.locked && <span>🔒</span>}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}

              {!hydratedImported.length && (
                <tr className={zebraRow()}>
                  <td className={td} colSpan={uploadBundle?.kind === "contest" ? 9 : 7}>
                    Upload a lineup CSV or contest entries CSV to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold">Swapped Results</div>
        <div className={cx("text-xs", T.subtext)}>
          {uploadBundle?.kind === "contest"
            ? "Selected contest rows will be updated on export; all other contest rows stay untouched."
            : "Preserves lineup order and locked slot positions exactly."}
        </div>
      </div>

      <div className={tableShell("mb-4 ls-card")}>
        <div className={scrollWrapClass + " pb-2"} style={{ maxHeight: 420 }}>
          <table className={fullTableClass("min-w-[1420px] text-xs")}>
            <thead>
              <tr>
                <th className={stickyThDark()}>#</th>
                {uploadBundle?.kind === "contest" && <th className={stickyThDark()}>Entry ID</th>}
                <th className={stickyThDark()}>Changed</th>
                <th className={stickyThDark()}>Salary</th>
                <th className={stickyThDark()}>Proj</th>
                <th className={stickyThDark()}>pOWN%</th>
                <th className={stickyThDark()}>Stack</th>
                <th className={stickyThDark()}>BO</th>
                <th className={stickyThDark("text-left")}>Final Players</th>
              </tr>
            </thead>

            <tbody>
              {visibleSwapped.map((lineup) => {
                const hitters = (lineup.finalAssignment || []).filter((x) => !x.isPitcher && x.name);
                const byTeam = new Map();
                hitters.forEach((h) => byTeam.set(h.team, (byTeam.get(h.team) || 0) + 1));
                const stackShape = Array.from(byTeam.values()).sort((a, b) => b - a).join("-") || "0-0";

                return (
                  <tr key={`swap:${lineup.id}`} className={zebraRow()}>
                    <td className={td}>{lineup.index}</td>

                    {uploadBundle?.kind === "contest" && (
                      <td className={td}>{lineup.entryId || "—"}</td>
                    )}

                    <td className={td}>{lineup.changed ? "Yes" : "No"}</td>
                    <td className={td}>{fmt0(lineup.finalSalary || 0)}</td>
                    <td className={td}>{fmt1(lineup.finalProj || 0)}</td>
                    <td className={td}>{fmt1(lineup.finalPown || 0)}</td>
                    <td className={td}>{stackShape}</td>

                    <td className={td}>
                      {(lineup.finalAssignment || [])
                        .filter((a) => !a.isPitcher && a.name)
                        .map((a) => a.row?.battingOrder || "-")
                        .join(" • ")}
                    </td>

                    <td className={td + " text-left"}>
                      <div className="flex flex-wrap gap-1">
                        {(lineup.finalAssignment || []).map((a) => (
                          <span
                            key={`${lineup.id}:${a.slot}:final`}
                            className={
                              "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 " +
                              (a.locked
                                ? isDark
                                  ? "bg-rose-500/10 border-rose-400/40 text-rose-200"
                                  : "bg-red-50 border-red-300 text-red-700"
                                : isDark
                                ? "bg-sky-900/20 border-sky-800 text-sky-200"
                                : "bg-blue-50 border-blue-200 text-blue-700")
                            }
                          >
                            <span className="font-semibold">{a.slot}</span>
                            <span>{a.name || "—"}</span>
                            {a.locked && <span>🔒</span>}
                          </span>
                        ))}
                      </div>

                      {!!lineup.backendError && (
                        <div className="mt-1 text-[11px] text-red-600">{lineup.backendError}</div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!visibleSwapped.length && (
                <tr className={zebraRow()}>
                  <td className={td} colSpan={uploadBundle?.kind === "contest" ? 10 : 9}>
                    Run Late Swap to generate swapped results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={tableShell("mb-4 ls-card")}>
        <div className={miniSectionTitle()}>
          <span>Saved Builds</span>
          <button className={ghostBtnClass} onClick={() => setSavedBuilds([])} disabled={!savedBuilds.length}>
            Clear Saved Builds
          </button>
        </div>

        <div className="overflow-auto" style={{ maxHeight: 260 }}>
          <table className="min-w-[980px] w-full text-xs">
            <thead>
              <tr>
                <th className={th}>Time</th>
                <th className={th}>Site</th>
                <th className={th}>Slate</th>
                <th className={th}>Type</th>
                <th className={th + " text-left"}>Contest</th>
                <th className={th}>Lineups</th>
                <th className={th}>Changed</th>
                <th className={th}>Errors</th>
                <th className={th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {savedBuilds.map((b) => (
                <tr key={b.id} className={zebraRow()}>
                  <td className={td}>{new Date(b.createdAt).toLocaleString()}</td>
                  <td className={td}>{String(b.site || "").toUpperCase()}</td>
                  <td className={td}>{String(b.slate || "").toUpperCase()}</td>
                  <td className={td}>{String(b.uploadKind || "").toUpperCase()}</td>
                  <td className={td + " text-left"}>{b.selectedContestName || "—"}</td>
                  <td className={td}>{b.lineupCount}</td>
                  <td className={td}>{b.changedCount}</td>
                  <td className={td}>{b.errorCount}</td>
                  <td className={td}>
                    <button
                      className={ghostBtnClass}
                      onClick={() => {
                        setSwappedLineups(b.results || []);
                        setStatusMsg(`Loaded saved build from ${new Date(b.createdAt).toLocaleString()}.`);
                      }}
                    >
                      Load
                    </button>
                  </td>
                </tr>
              ))}

              {!savedBuilds.length && (
                <tr className={zebraRow()}>
                  <td className={td} colSpan={9}>No saved builds yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(min(360px, 100%), 1fr))",
        }}
      >
        {visibleSwapped.map((lineup) => (
          <div key={`card:${lineup.id}`} className={cx(cardClass, "p-3 ls-card")}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-base font-semibold">
                Lineup #{lineup.index}
                {uploadBundle?.kind === "contest" && lineup.entryId ? ` · ${lineup.entryId}` : ""}
              </div>

              <div className={cx("text-[11px]", T.subtext)}>
                {lineup.changed ? "Changed" : "Unchanged"}
              </div>
            </div>

            <div className={cx("overflow-hidden rounded-lg border", isDark ? "border-slate-800" : "border-slate-200")}>
              <table className="w-full table-fixed text-xs">
                <thead>
                  <tr>
                    <th className="text-left py-1 px-2 w-[48px]">Slot</th>
                    <th className="text-center py-1 px-2 w-[36px]">BO</th>
                    <th className="text-left py-1 px-2">Player</th>
                    <th className="text-right py-1 px-2 w-[58px]">Proj</th>
                    <th className="text-right py-1 px-2 w-[64px]">pOWN%</th>
                    <th className="text-right py-1 px-2 w-[58px]">Sal</th>
                  </tr>
                </thead>

                <tbody>
                  {(lineup.finalAssignment || []).map((a, j) => (
                    <tr key={`${lineup.id}:${a.slot}:${j}`} className={zebraRow()}>
                      <td className="py-1 px-2">{a.slot}</td>
                      <td className="py-1 px-2 text-center">
                        {a.isPitcher ? "-" : (a.row?.battingOrder || "-")}
                      </td>

                      <td className="py-1 px-2 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          {a.team && <span style={chipStyle(a.team)}>{a.team}</span>}
                          <span className="truncate">{a.name || "—"}</span>
                          {a.locked && <span title="Locked">🔒</span>}
                        </div>
                      </td>

                      <td className="py-1 px-2 text-right tabular-nums">{fmt1(a.proj || 0)}</td>
                      <td className="py-1 px-2 text-right tabular-nums">{fmt1((a.pown || 0) * 100)}</td>
                      <td className="py-1 px-2 text-right tabular-nums">{fmt0(a.salary || 0)}</td>
                    </tr>
                  ))}

                  <tr className={cx("font-semibold", isDark ? "bg-slate-950/80" : "bg-slate-100")}>
                    <td className="py-1 px-2">Totals</td>
                    <td />
                    <td />
                    <td className="py-1 px-2 text-right tabular-nums">{fmt1(lineup.finalProj || 0)}</td>
                    <td className="py-1 px-2 text-right tabular-nums">{fmt1(lineup.finalPown || 0)}</td>
                    <td className="py-1 px-2 text-right tabular-nums">{fmt0(lineup.finalSalary || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}